import { useMaestroStore } from '../store/maestroStore';
import { useQobuzStore } from '../lib/useQobuz';
import { TransportCommand } from './TransportCommand';
import { isDsd } from '../utils/formatDetection';
import {
  isLocalLibraryTrack,
  isLocalTrackId,
  resolveLocalFileUrl,
  prefetchNextLocalTracks,
  isLocalStreamUrl,
} from '../lib/playback-bridge';
import {
  isLimitedRenderer,
  getFormatIdForRenderer,
  shouldSkipGaplessPrefetch,
  isTimerBasedRenderer,
} from './types';
import {
  setGaplessPrefetchIndex,
  clearGaplessPrefetchIndex,
  markRendererGaplessAdvance,
  cancelRemoteAdvanceTimer,
  bumpAdvanceGeneration,
  getAdvanceGeneration,
  markSetNextFillConfirmed,
  clearSetNextFillConfirmed,
  isSetNextFillConfirmedForQueueIndex,
  getGaplessPrefetchIndex,
  isRendererGaplessAdvanceRecent,
} from './remoteAdvanceControl';
import {
  bumpQueueReplaceGeneration,
  countQueueReplaceSurvivors,
  trackIdsFromQueue,
} from './queueReplaceEpoch';
import { connectorRendererFromZone, isGaplessSetNextEnabled, rendererSupportsSetNext, shouldAttemptSetNextPrefetch } from './gaplessSetNext';
import { connectorSupportsLocalLibrarySetNext } from '../utils/connectorLocalSetNext';
import { clearPlaybackNetworkFormatLabel } from '../utils/playbackNetworkFormatLabel';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { showImperativeToast } from '../providers/UIProvider';
import { routeStream, getMultizoneFormatId } from '../services/StreamRouter';
import { isIOSNativeApp, detectPlatform } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { logSetNextPrefetchOutcome } from './stopEvidenceBeacons';
import { logClearQueueTransitionIfSlow } from '../utils/clearQueueDiagnostics';
import { markUserPlaybackStarted } from './playbackSession';
import { ensurePlaybackZoneBeforeStart } from './zoneRestorePick';
import { TransportState } from './TransportState';
import { commitQueueOwnerPosition } from './queueOwnerCommit';
import {
  applyResolvedTrackDuration,
  backfillQueueRowDuration,
  beginPlayTrackToken,
  dropSupersededPlay,
  isPlayTrackCurrent,
  resolveQueueTrackDuration,
} from './queuePlayTrackGuards';
import { GAPLESS_HANDOFF_VERIFY_DELAY_MS, verifyGaplessHandoffPlayback } from './gaplessHandoffVerify';
import { shouldHonorQueueTrackEndAdvance } from './queueTransportOwnership';
import { allowAdvanceForExclusiveEngineEnd } from './exclusiveEngineAdvanceGuard';
import { allowTrustedConnectorTrackEnd } from './trustedConnectorTrackEnd';
import { fisherYatesShuffle } from './queueFisherYates';
import {
  notePlayUriDispatched,
  noteTrackSelectionStarted,
  buildAdvanceSuppressedWallClockLog,
  evaluateWallClockAdvanceGuard,
  getAdvanceEpochTrackId,
  getEffectiveStartOffsetSecs,
  getSincePlayUriMs,
  hasObservedPositionProgressOnCurrentTrack,
  isInsideStartGrace,
  isPositionAtGenuineTrackEnd,
  recordFrozenEndWallClockSuppression,
  shouldAllowTimerPathAdvance,
  startGraceSuppressReason,
  START_GRACE_MS,
} from './positionReconcile';
import {
  isUserTransportStopRecent,
  isScheduledAdvanceTokenValid,
} from './remoteAdvanceControl';

import {
  isRemoteRendererZone,
  syncRendererConnectivityFromZones,
} from './rendererConnectivity';
import { useOperationProgressStore } from '../store/operationProgressStore';
import { handleLocalPlayFailure } from '../utils/localPlayFailureUI';
import {
  markNetworkPlayAttemptStarted,
  markNetworkPlaySucceeded,
  markTransportPlayFailure,
} from '../utils/proxyFetchFailedUI';
import {
  showNetworkPlayFailureToast,
  showUserPlayRetryToast,
} from '../utils/playFailureUI';
import { runQueueStreamResolutionFailureFor } from './queueStreamResolutionBridge';
import {
  handleQueuePlayFailure,
  type PriorPlaybackSnapshot,
} from './queueControllerPlayFailure';
import {
  noteProxyUnreachableRecovery,
  scheduleLocalProxyUnreachableRetry,
  scheduleStreamingProxyUnreachableRetry,
} from './streamProxyUnreachableRetry';
import {
  clearPlayUriPending, clearQueueOwnershipAcceptanceLatch, clearQueueOwnershipLatchForNewTrack,
  isPlayUriPending,
  shouldBlockDuplicatePlayUri,
  shouldRetainQueueTransportOwnership,
} from './playUriConfirmation';
import { deferUntilPlayUriConfirmed } from './playUriStartGates'; import { isPlayUriNotSentError } from './playUriSupersede'; import { formatReplayStaleReason as replayStaleWhy } from './formatCeilingReplayGuard'; import { beginFormatAttempt, cancelFormatCeilingReplay, formatWithinLearnedCeiling, replayAtLowerFormatIfCeiling, noteSupersededFormatFailure, type FormatAttempt } from './formatCeiling';
import { recordLocalPlaySuccess } from '../utils/unmountedDriveHeuristic';
import { durationFromTrackRow, normalizeTrackDurationSecs, prepareTrackForQueue, isAdvanceGuardDurationTrustworthy } from '../utils/trackDuration';
import {
  resolveExclusiveDeviceName,
  logLocalOutputShared,
} from '../utils/localOutputLogging';
import {
  clearExclusiveErrorOnUserPlayStart,
  runExclusiveSharedFallback,
} from '../utils/exclusiveFallbackUI';
import { commitExclusiveDispatchOutputPath } from '../utils/sharedModeNotice';
import {
  normalizeRecentAlbumId,
  saveRecentAlbumFromTrack,
} from '../store/recentAlbumsStore';
import { filterForRadioEnqueue, isRadioQueueSession, countRadioRegistryUnplayedBeyondQueue } from '../radio/radioTrackRegistry';
import {
  getRadioSessionBuildStatus,
  shouldDeferRadioQueueTerminal,
} from '../radio/ellaRadioSessionState';

/**
 * Pick a Qobuz-style format_id for a local library track. This mirrors the
 * convention used throughout the playback layer:
 *   7  → lossy (MP3/AAC/M4A)
 *   6  → 16-bit/44.1kHz lossless (CD quality)
 *   27 → hi-res lossless (>16/44)
 *
 * Multizone groups negotiate their own format via getMultizoneFormatId().
 * Limited renderers (no hi-res support) are pinned to 6 for lossless content.
 */
function pickFormatIdForLocalFile(
  track: any,
  renderer: any,
  isMultizone: boolean,
): number {
  const fmt = String(track?.format || '').toUpperCase();
  if (fmt === 'MP3' || fmt === 'AAC' || fmt === 'M4A') return 7;

  const rate = Number(track?.sample_rate || 0);
  const depth = Number(track?.bit_depth || 0);
  const isHires = (rate > 44100 && depth >= 24) || rate > 48000;

  if (isMultizone) {
    return isHires ? getMultizoneFormatId() : 6;
  }
  if (isLimitedRenderer(renderer)) return 6;
  return isHires ? 27 : 6;
}

/** Cooldown between _playTrack completions — blocks rapid duplicate dispatch (show-floor / quality-prefetch races). */
let _lastPlayTrackCompletedAt = 0;

/** Elapsed ms since `_playTrack` last finished (`Infinity` if never completed). Used by ghost playback watchdogs. */
export function msSinceLastPlayTrackCompleted(): number {
  return _lastPlayTrackCompletedAt === 0 ? Infinity : Date.now() - _lastPlayTrackCompletedAt;
}

// Test-playback mode: when active, the playing→stopped failure handler
// is suppressed so the Devices page can show its own format-specific UI.
let _testPlaybackMode = false;
export function setTestPlaybackMode(active: boolean) { _testPlaybackMode = active; }
export function isTestPlaybackMode(): boolean { return _testPlaybackMode; }

function isLocalNativeZone(zone: { id?: string; type?: string } | null | undefined): boolean {
  if (!zone) return false;
  if (zone.type === 'local-native') return true;
  return typeof zone.id === 'string' && zone.id.startsWith('local-native:');
}

/** Desktop exclusive-engine target only — mobile keeps pre-4a11b6d8 renderer.id dispatch. */
function isTauriLocalNativeZone(zone: { id?: string; type?: string } | null | undefined): boolean {
  return detectPlatform().isTauriDesktop && isLocalNativeZone(zone);
}

function _isRemoteRenderer(): boolean {
  const store = useMaestroStore.getState();
  const az = store.discovery?.activeZone;
  // Bit-perfect USB DAC zones use the exclusive engine — never UPnP play_local_file IPC.
  if (isTauriLocalNativeZone(az) || (az && !isRemoteRendererZone(az))) return false;

  if (isRemoteRendererZone(az)) return true;

  const renderer = store.activeConnectorRenderer;
  if (!renderer?.id) return false;
  if (renderer.id === 'this_device' || renderer.id === 'this_computer' || renderer.id === 'local_computer') {
    return false;
  }
  if (isTauriLocalNativeZone({ id: renderer.id, type: undefined })) return false;
  const discovered = store.discovery?.discoveredZones?.find(
    (z: { id?: string; type?: string }) => z.id === renderer.id,
  );
  if (isTauriLocalNativeZone(discovered)) return false;
  return isRemoteRendererZone(discovered) || true;
}

/** Why _playTrack was invoked — drives failure recovery (pause+retry vs auto-skip). */
type PlayIntent = 'user' | 'queue-advance';

/** Why advance suppression was armed — used in diagnostics. */
export type SuppressAdvanceReason =
  | 'zone_switch'
  | 'user_skip'
  | 'gapless_adopt'
  | 'ownership_transfer'
  | 'clear_queue'
  | 'default';

type SuppressedEndRecheck = {
  generation: number;
  queueIndex: number;
  rendererId: string;
  timer: ReturnType<typeof setTimeout>;
};

/** Single owner for play-failure recovery — prevents racing advance paths. */
type FailureRecoveryOwner = 'none' | 'user-pause' | 'queue-skip';

class QueueControllerImpl {
  private _initialized = false;
  private _unsubscribe: (() => void) | null = null;
  private _suppressUntil = 0;
  private _suppressReason: SuppressAdvanceReason = 'default';
  private _suppressedEndRecheck: SuppressedEndRecheck | null = null;

  private _lastPlayUriTime = 0;
  private _lastPlayUriFormatId = 0;
  private _failAdvancing = false;
  /** Prevents reentrant _advance when the store subscriber fires mid-await. */
  private _advancing = false;

  private _consecutiveFails = 0;
  private _consecutiveFailWindowStart = 0;

  private _consecutiveSkips = 0;

  /** Prevents overlapping _playTrack runs (watchdog + user) from racing two playUri calls to the renderer. */
  private _isPlayTrackInFlight = false;
  private _inFlightTrackId: string | null = null;

  /** Queue index of the track that was pre-buffered via SetNextAVTransportURI for gapless playback. */
  private _prebufferedForQueueIndex: number = -1;

  private _recordPlayUriDispatch(startSeconds?: number | null): void {
    this._lastPlayUriTime = Date.now();
    notePlayUriDispatched(startSeconds);
    const store = useMaestroStore.getState();
    const startPos = startSeconds != null && startSeconds > 0
      ? Math.floor(startSeconds)
      : 0;
    if (store.position !== startPos) {
      store.updateTime(startPos, store.duration);
    }
  }

  /** Intent for the in-flight or most recent _playTrack dispatch. */
  private _playIntent: PlayIntent = 'user';

  /** Exactly one failure-recovery decision per play attempt. */
  private _failureRecoveryOwner: FailureRecoveryOwner = 'none';
  private _failureRecoveryReleaseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Watchdog when SetNext handoff suppresses playUri at track boundary. */
  private _setNextHandoffWatchdog: ReturnType<typeof setTimeout> | null = null;
  private static readonly SETNEXT_HANDOFF_WATCHDOG_MS = 8000;

  /** Verify timer-based SetNext handoff actually started on the renderer (PS Audio class). */
  private _gaplessHandoffVerifyTimer: ReturnType<typeof setTimeout> | null = null;

  /** Pre-resolved stream URL for the next track (no-SetNext renderers only — URL only, no SetNext). */
  private _prefetchedStream: {
    queueIndex: number;
    trackId: string;
    url: string;
    formatId: number;
    format?: string;
    samplingRate?: number;
    bitDepth?: number;
  } | null = null;

  /** Set when playUri fails with "not found" — triggers auto-recovery when renderer returns */
  private _rendererLostAt = 0;
  private _rendererLostId: string | null = null;

  /** Last queue-state diagnostic fingerprint — log only on change. */
  private _lastQueueStateDiagKey: string | null = null;
  /** Suppress repeated end-of-queue advance work while already terminal. */
  private _queueTerminalHandled = false;
  /** Dedupe rapid duplicate renderer-track-ended for the same track boundary. */
  private _lastTrackEndDedupeKey: string | null = null;
  private _lastTrackEndDedupeAt = 0;
  private static readonly TRACK_END_DEDUPE_MS = 500;
  /** Stalled-start retry when renderer stays STOPPED early in a track. */
  private _stalledStartTimer: ReturnType<typeof setTimeout> | null = null;
  private _stalledStartGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private _stalledStartRetriedTrackId: string | null = null;
  private _trackReachedPlayingId: string | null = null;
  private static readonly STALLED_START_RETRY_MS = 20_000;
  /**
   * clear_queue suppress is scoped to the replace/teardown window only.
   * Never 120s — a stuck flag stalls Ella/Sonos track-end advance for minutes.
   */
  private static readonly CLEAR_QUEUE_SUPPRESS_MS = 5_000;
  /**
   * Track-end during a queue replace must re-check within ~1–2s, not when the
   * full suppress window expires (field: minutes of silence then double-fetch).
   */
  private static readonly MAX_SUPPRESSED_END_RECHECK_MS = 1_500;
  /** Dedupes Recently Played writes within one album queue session (auto-advance). */
  private _recentLocalAlbumSavedKey: string | null = null;

  /** Suppress the playing→stopped advance for `ms` milliseconds (teardown / handoff phantoms). */
  suppressAdvance(ms = 3000, reason: SuppressAdvanceReason = 'default') {
    // A new suppress arm supersedes any pending end re-check from a prior arm.
    if (this._suppressedEndRecheck) {
      clearTimeout(this._suppressedEndRecheck.timer);
      this._suppressedEndRecheck = null;
    }
    this._suppressUntil = Date.now() + ms;
    this._suppressReason = reason;
    console.log('[QueueController] advance suppressed', { reason, remainingMs: ms });
  }

  private _clearAdvanceSuppression(clearReason: string): void {
    if (this._suppressUntil <= 0) return;
    console.log('[QueueController] advance suppression cleared', {
      clearReason,
      priorReason: this._suppressReason,
    });
    this._suppressUntil = 0;
    this._suppressReason = 'default';
    if (this._suppressedEndRecheck) {
      clearTimeout(this._suppressedEndRecheck.timer);
      this._suppressedEndRecheck = null;
    }
  }

  /** Drop clear_queue suppress when the replacement queue is installed / play dispatched. */
  private _clearQueueReplaceSuppression(clearReason: string): void {
    if (this._suppressUntil <= 0) return;
    if (this._suppressReason !== 'clear_queue') return;
    this._clearAdvanceSuppression(clearReason);
  }

  private _maybeClearSuppressionOnConfirmedPlay(
    state: ReturnType<typeof useMaestroStore.getState>,
    prevState: ReturnType<typeof useMaestroStore.getState>,
  ): void {
    if (this._suppressUntil <= 0) return;
    if (!TransportState.isQueueOwner()) return;

    const rendererId = state.activeConnectorRenderer?.id;
    if (!rendererId) return;
    if (state.playbackState !== 'playing') return;

    const trackId = state.currentTrack?.id;
    if (!trackId) return;

    // clear_queue: identity-only clear never fires (currentTrack is set before
    // loading→playing). Clear on the playing edge once a replacement queue exists
    // — never on clearQueue's intermediate setQueue([],0) while still 'playing'.
    if (this._suppressReason === 'clear_queue') {
      if (prevState.playbackState === 'playing') return;
      if (!state.queue.length) return;
      this._clearAdvanceSuppression('confirmed-playback');
      return;
    }

    const isNewTrack =
      String(trackId) !== String(prevState.currentTrack?.id ?? '')
      || state.queueIndex !== prevState.queueIndex;
    if (!isNewTrack) return;

    this._clearAdvanceSuppression('confirmed-playback');
  }

  private _scheduleSuppressedEndRecheck(snapshot: {
    generation: number;
    queueIndex: number;
    rendererId: string;
  }): void {
    if (!snapshot.rendererId) return;

    if (this._suppressedEndRecheck) {
      clearTimeout(this._suppressedEndRecheck.timer);
    }

    const remainingMs = Math.max(0, this._suppressUntil - Date.now());
    // Replace-scoped suppress must not defer the end re-check for the full window.
    const delayMs = this._suppressReason === 'clear_queue'
      ? Math.min(remainingMs, QueueControllerImpl.MAX_SUPPRESSED_END_RECHECK_MS)
      : remainingMs;
    const timer = setTimeout(() => {
      void this._onSuppressedEndRecheckExpiry(snapshot);
    }, delayMs);

    this._suppressedEndRecheck = { ...snapshot, timer };
  }

  private async _onSuppressedEndRecheckExpiry(snapshot: {
    generation: number;
    queueIndex: number;
    rendererId: string;
  }): Promise<void> {
    this._suppressedEndRecheck = null;

    if (!TransportState.isQueueOwner()) return;
    if (getAdvanceGeneration() !== snapshot.generation) return;

    const store = useMaestroStore.getState();
    if (store.queueIndex !== snapshot.queueIndex) return;
    if (store.activeConnectorRenderer?.id !== snapshot.rendererId) return;

    // End already happened during replace — drop a stuck clear_queue flag so
    // advanceTrackEnd can run instead of re-arming another long suppress.
    if (this._suppressReason === 'clear_queue' && Date.now() < this._suppressUntil) {
      this._clearAdvanceSuppression('suppressed-end-recheck');
    }

    if (store.playbackState !== 'stopped') return;

    console.log('[QueueController] suppressed end re-check — advancing', {
      generation: snapshot.generation,
      queueIndex: snapshot.queueIndex,
      rendererId: snapshot.rendererId,
    });

    await this.advanceTrackEnd();
  }

  private _handleAdvanceSuppressed(
    path: 'renderer-track-ended' | 'advanceTrackEnd',
    reconcilePath: string,
    context?: { generation?: number; queueIndex?: number; rendererId?: string },
  ): boolean {
    if (Date.now() >= this._suppressUntil) return false;

    if (this._maybeReconcileRendererAheadOfEpoch(reconcilePath)) {
      return true;
    }

    const store = useMaestroStore.getState();
    this._scheduleSuppressedEndRecheck({
      generation: context?.generation ?? getAdvanceGeneration(),
      queueIndex: context?.queueIndex ?? store.queueIndex,
      rendererId: context?.rendererId ?? store.activeConnectorRenderer?.id ?? '',
    });

    const remainingMs = Math.max(0, this._suppressUntil - Date.now());
    console.log(`[QueueController] ${path} suppressed (${this._suppressReason})`, {
      reason: this._suppressReason,
      remainingMs,
    });
    return true;
  }

  private _clearStalledStartWatch(): void {
    if (this._stalledStartTimer) {
      clearTimeout(this._stalledStartTimer);
      this._stalledStartTimer = null;
    }
    if (this._stalledStartGraceTimer) {
      clearTimeout(this._stalledStartGraceTimer);
      this._stalledStartGraceTimer = null;
    }
  }

  private _emitEarlyAdvanceSuppressed(
    reason: string,
    extras?: Record<string, unknown>,
  ): void {
    diagnosticLog('info', 'playback', 'early_advance_suppressed', {
      reason,
      sincePlayUri: getSincePlayUriMs(),
      ...extras,
    });
  }

  private _resolveTrackDurationSecs(store = useMaestroStore.getState()): number | undefined {
    const fromStore = normalizeTrackDurationSecs(store.duration);
    if (fromStore != null) return fromStore;
    const fromCurrent = durationFromTrackRow(store.currentTrack);
    if (fromCurrent != null) return fromCurrent;
    return durationFromTrackRow(store.queue[store.queueIndex]?.track);
  }

  /** Queue play-order index for a track id (-1 if not in queue). */
  private _queueIndexForTrackId(
    trackId: string,
    store = useMaestroStore.getState(),
  ): number {
    const id = String(trackId);
    const { queue, shuffled, shuffleOrder } = store;
    for (let qi = 0; qi < queue.length; qi++) {
      const actualIdx = shuffled && shuffleOrder.length > qi ? shuffleOrder[qi] : qi;
      const t = queue[actualIdx]?.track;
      if (t && String(t.id) === id) return qi;
    }
    return -1;
  }

  /**
   * Renderer gaplessly advanced ahead of the selection epoch — stale end/stop events
   * for the prior track must not dead-end. Sync epoch and resume without playUri.
   */
  private _reconcileRendererAheadOfEpoch(
    path: string,
    currentTrackId: string,
    epochTrackId: string,
  ): void {
    const store = useMaestroStore.getState();
    const currentIdx = this._queueIndexForTrackId(currentTrackId, store);
    const epochIdx = this._queueIndexForTrackId(epochTrackId, store);
    if (currentIdx < 0 || epochIdx < 0 || currentIdx <= epochIdx) return;

    console.log('[QueueController] renderer ahead of epoch — reconciling to current track', {
      path,
      epochTrackId,
      currentTrackId,
      epochIdx,
      currentIdx,
    });

    noteTrackSelectionStarted(currentTrackId);
    this._clearSetNextHandoffWatchdog();
    clearGaplessPrefetchIndex();
    clearSetNextFillConfirmed();
    this._prebufferedForQueueIndex = -1;
    this._clearStalledStartWatch();

    if (store.queueIndex !== currentIdx) {
      const { queue, shuffled, shuffleOrder } = store;
      const actualIdx = shuffled && shuffleOrder.length > currentIdx
        ? shuffleOrder[currentIdx]
        : currentIdx;
      const item = queue[actualIdx];
      if (item?.track) {
        store.setQueueIndex(currentIdx);
        store.setCurrentTrack(item.track);
      }
    }

    if (store.playbackState === 'stopped' || store.playbackState === 'paused') {
      store.setPlaybackState('playing');
    }
  }

  /** True when queue was reconciled to a renderer-ahead track (no playUri / no advance). */
  private _maybeReconcileRendererAheadOfEpoch(path: string): boolean {
    const store = useMaestroStore.getState();
    const epochTrackId = getAdvanceEpochTrackId();
    const currentId = store.currentTrack?.id;
    if (!epochTrackId || !currentId || epochTrackId === String(currentId)) return false;
    const before = epochTrackId;
    this._reconcileRendererAheadOfEpoch(path, String(currentId), epochTrackId);
    return getAdvanceEpochTrackId() !== before;
  }

  /**
   * Wall-clock gate for timer/stopped advance paths. Returns true when advance may proceed.
   * When false, arms stalled-start retry (unless user stopped).
   */
  private _guardTimerPathAdvance(
    path: string,
    rendererId?: string,
    capturedGen?: number,
    capturedTrackId?: string | null,
    options?: {
      bypassWallClockAtGenuineEnd?: boolean;
      trackEndSource?: string;
      trustExclusiveEngineEnd?: boolean;
    },
  ): boolean {
    if (isUserTransportStopRecent()) {
      return false;
    }

    if (allowAdvanceForExclusiveEngineEnd(
      options?.trustExclusiveEngineEnd,
      path,
      rendererId,
      capturedTrackId,
      () => this._clearStalledStartWatch(),
    )) {
      return true;
    }

    const store = useMaestroStore.getState();
    if (store.playbackState === 'paused') {
      return false;
    }
    const trackId = capturedTrackId
      ?? store.currentTrack?.id
      ?? store.queue[store.queueIndex]?.track?.id
      ?? getAdvanceEpochTrackId();
    const gen = capturedGen ?? getAdvanceGeneration();

    const currentTrackIdForValidation = store.currentTrack?.id
      ?? store.queue[store.queueIndex]?.track?.id
      ?? getAdvanceEpochTrackId();

    if (!isScheduledAdvanceTokenValid(gen, trackId != null ? String(trackId) : null, currentTrackIdForValidation != null ? String(currentTrackIdForValidation) : null)) {
      console.log('[QueueController] timer-path advance stale — generation or track moved', {
        path,
        capturedGen: gen,
        trackId,
        currentTrackId: store.currentTrack?.id,
        generation: getAdvanceGeneration(),
      });
      return false;
    }

    const epochTrackId = getAdvanceEpochTrackId();
    if (epochTrackId && trackId && epochTrackId !== String(trackId)) {
      const storeCurrentId = currentTrackIdForValidation != null
        ? String(currentTrackIdForValidation)
        : null;
      if (storeCurrentId && this._maybeReconcileRendererAheadOfEpoch(path)) {
        return false;
      }
      console.log('[QueueController] timer-path advance stale — epoch track mismatch', {
        path,
        epochTrackId,
        trackId,
      });
      return false;
    }

    if (allowTrustedConnectorTrackEnd({
      source: options?.trackEndSource,
      path,
      rendererId: rendererId || store.activeConnectorRenderer?.id || 'unknown',
      trackId: trackId != null ? String(trackId) : null,
      durationSecs: this._resolveTrackDurationSecs(store),
      position: store.position || 0,
      clearStalledStartWatch: () => this._clearStalledStartWatch(),
    })) {
      return true;
    }

    const durationSecs = this._resolveTrackDurationSecs(store);
    if (!isAdvanceGuardDurationTrustworthy(durationSecs)) {
      diagnosticLog(
        'info',
        'playback',
        '[playback] advance_guard_no_duration',
        {
          trackId: trackId != null ? String(trackId) : null,
          source: path,
        },
      );
      this._clearStalledStartWatch();
      return true;
    }

    const positionSecs = store.position || 0;
    const sincePlayUriMs = getSincePlayUriMs();
    const effectiveStartOffsetSecs = getEffectiveStartOffsetSecs();

    if (
      options?.bypassWallClockAtGenuineEnd
      && isPositionAtGenuineTrackEnd(positionSecs, durationSecs)
    ) {
      if (isInsideStartGrace(sincePlayUriMs) && !hasObservedPositionProgressOnCurrentTrack()) {
        this._emitEarlyAdvanceSuppressed(
          startGraceSuppressReason({
            sincePlayUriMs,
            positionSecs,
            durationSecs: durationSecs ?? 0,
          }) ?? 'stopped_without_progress',
          {
            path,
            trackId: trackId != null ? String(trackId) : null,
            position: positionSecs,
            duration: durationSecs,
          },
        );
        this._maybeArmStalledStartWatch(path, gen, trackId != null ? String(trackId) : null);
        return false;
      }
      this._clearStalledStartWatch();
      return true;
    }

    const guard = evaluateWallClockAdvanceGuard({
      durationSecs,
      sincePlayUriMs,
      effectiveStartOffsetSecs,
      positionSecs,
      rendererState: 'STOPPED',
    });

    if (guard.allow) {
      if (guard.wallClockEnd) {
        diagnosticLog(
          'info',
          'playback',
          '[playback] advance reason=wall_clock_end',
          {
            path: 'stopped_timer',
            trackId: trackId != null ? String(trackId) : null,
            rendererId: rendererId || store.activeConnectorRenderer?.id || 'unknown',
            position: positionSecs,
            duration: durationSecs,
            sincePlayUri: sincePlayUriMs,
            effective_start_offset: effectiveStartOffsetSecs,
          },
        );
      } else if (guard.forcedAtFrozenEnd) {
        diagnosticLog(
          'info',
          'playback',
          '[playback] advance_forced_after_frozen_end',
          {
            path: 'stopped_timer',
            trackId: trackId != null ? String(trackId) : null,
            rendererId: rendererId || store.activeConnectorRenderer?.id || 'unknown',
            position: positionSecs,
            duration: durationSecs,
            priorSuppressionCount: guard.priorSuppressionCount,
            sincePlayUri: sincePlayUriMs,
            effective_start_offset: effectiveStartOffsetSecs,
          },
        );
      }
      this._clearStalledStartWatch();
      return true;
    }

    if (guard.atGenuineEnd) {
      recordFrozenEndWallClockSuppression();
    }

    diagnosticLog(
      'info',
      'playback',
      '[playback] advance_suppressed_wall_clock',
      buildAdvanceSuppressedWallClockLog(
        rendererId || store.activeConnectorRenderer?.id || 'unknown',
        positionSecs,
        durationSecs ?? 0,
        'STOPPED',
        sincePlayUriMs,
        effectiveStartOffsetSecs,
        { path: 'stopped_timer', trackId: trackId != null ? String(trackId) : null },
      ),
    );
    if (isInsideStartGrace(sincePlayUriMs) && !hasObservedPositionProgressOnCurrentTrack()) {
      this._emitEarlyAdvanceSuppressed(
        startGraceSuppressReason({
          sincePlayUriMs,
          positionSecs,
          durationSecs: durationSecs ?? 0,
        }) ?? 'stopped_without_progress',
        {
          path,
          trackId: trackId != null ? String(trackId) : null,
          position: positionSecs,
          duration: durationSecs,
        },
      );
    }
    this._maybeArmStalledStartWatch(path, gen, trackId != null ? String(trackId) : null);
    return false;
  }

  private _maybeArmStalledStartWatch(
    source: string,
    capturedGen: number,
    capturedTrackId: string | null,
  ): void {
    if (isUserTransportStopRecent()) return;
    if (!capturedTrackId || !_isRemoteRenderer()) return;
    if (this._stalledStartTimer) return;
    // Track already reached PLAYING once — stalled-start retry is for never-started tracks only.
    if (this._trackReachedPlayingId === capturedTrackId) return;

    // #574: do not start the 20s re-dispatch inside the start window unless
    // RelTime moved on this track (Hansen triple-start / advanceTrackEnd).
    if (isInsideStartGrace() && !hasObservedPositionProgressOnCurrentTrack()) {
      if (this._stalledStartGraceTimer) return;
      const remainingMs = Math.max(0, START_GRACE_MS - getSincePlayUriMs());
      this._stalledStartGraceTimer = setTimeout(() => {
        this._stalledStartGraceTimer = null;
        this._maybeArmStalledStartWatch(source, capturedGen, capturedTrackId);
      }, remainingMs);
      return;
    }

    this._stalledStartTimer = setTimeout(() => {
      this._stalledStartTimer = null;
      void this._onStalledStartRetryDeadline(source, capturedGen, capturedTrackId);
    }, QueueControllerImpl.STALLED_START_RETRY_MS);
  }

  private async _onStalledStartRetryDeadline(
    source: string,
    capturedGen: number,
    capturedTrackId: string,
  ): Promise<void> {
    if (isUserTransportStopRecent()) return;
    if (!isScheduledAdvanceTokenValid(capturedGen, capturedTrackId, useMaestroStore.getState().currentTrack?.id)) {
      return;
    }

    const store = useMaestroStore.getState();
    if (store.playbackState === 'playing' || this._trackReachedPlayingId === capturedTrackId) {
      return;
    }

    const durationSecs = this._resolveTrackDurationSecs(store);
    if (shouldAllowTimerPathAdvance(durationSecs)) {
      return;
    }

    if (this._stalledStartRetriedTrackId === capturedTrackId) {
      console.warn('[QueueController] stalled start — playback failed after retry', {
        trackId: capturedTrackId,
        source,
      });
      diagnosticLog('error', 'playback', '[playback] stalled_start_failed', {
        trackId: capturedTrackId,
        rendererId: store.activeConnectorRenderer?.id,
        sincePlayUriMs: getSincePlayUriMs(),
        source,
      });
      this._playIntent = 'queue-advance';
      this._handlePlayUriFailed();
      return;
    }

    this._stalledStartRetriedTrackId = capturedTrackId;
    diagnosticLog('info', 'playback', '[playback] stalled_start_retry', {
      trackId: capturedTrackId,
      rendererId: store.activeConnectorRenderer?.id,
      sincePlayUriMs: getSincePlayUriMs(),
      attempt: 1,
      source,
    });

    const item = store.queue[store.queueIndex];
    if (item?.track && String(item.track.id) === capturedTrackId) {
      await this._playTrack(item.track, { intent: 'queue-advance' });
    }
  }

  private _clearRecentLocalAlbumSession(): void {
    this._recentLocalAlbumSavedKey = null;
  }

  /** Record local album in Recently Played once playback actually starts (not on hydrate-only store updates). */
  private _recordRecentLocalAlbumOnPlaybackStart(track: any): void {
    const albumKey = normalizeRecentAlbumId(
      track?.albumId || track?.album_key || track?.album_id,
      'local_library',
    );
    if (!albumKey) {
      diagnosticLog('warn', 'navigation', '[recents] save fail source=local reason=missing_album_id', {
        track_id: track?.id || null,
      });
      return;
    }
    if (this._recentLocalAlbumSavedKey === albumKey) return;
    this._recentLocalAlbumSavedKey = albumKey;

    saveRecentAlbumFromTrack({
      ...track,
      source: 'local_library',
      albumId: track.albumId || track.album_key || albumKey,
      album_key: track.album_key || albumKey,
      album: track.album || track.albumTitle || '',
      embedded_cover_hash: track.embedded_cover_hash ?? track.embeddedCoverHash ?? null,
      folder_cover_hash: track.folder_cover_hash ?? track.folderCoverHash ?? null,
    });
  }

  private _logQueueStateIfChanged(
    remaining: number,
    totalLoaded: number,
    context: string,
  ): void {
    const key = `${remaining}|${totalLoaded}|${context}`;
    if (key === this._lastQueueStateDiagKey) return;
    this._lastQueueStateDiagKey = key;
    diagnosticLog('warn', 'playback', `Queue state: ${remaining} tracks remaining`, {
      remaining,
      totalLoaded,
      context,
    });
  }

  private _clearQueueTerminalLatch(): void {
    this._queueTerminalHandled = false;
    this._lastQueueStateDiagKey = null;
  }

  private _clearPrefetchedStream(): void {
    this._prefetchedStream = null;
  }

  private _abortGaplessPrefetch(): void {
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    clearSetNextFillConfirmed();
    this._clearSetNextHandoffWatchdog();
    this._clearGaplessHandoffVerify();
  }

  private _clearSetNextHandoffWatchdog(): void {
    if (this._setNextHandoffWatchdog) {
      clearTimeout(this._setNextHandoffWatchdog);
      this._setNextHandoffWatchdog = null;
    }
  }

  private _clearGaplessHandoffVerify(): void {
    if (this._gaplessHandoffVerifyTimer) {
      clearTimeout(this._gaplessHandoffVerifyTimer);
      this._gaplessHandoffVerifyTimer = null;
    }
  }

  /**
   * Timer-based SetNext renderers (PS Audio, AirLens, …) adopt the next queue track
   * optimistically at boundary. If the device is not playing shortly after, play the adopted
   * track explicitly — never a bare Play, which replays the device's old URI (#11309).
   */
  private _scheduleGaplessHandoffPlaybackVerify(capturedGen: number, queueIndex: number): void {
    if (!(window as any).__TAURI__) return;

    this._clearGaplessHandoffVerify();
    this._gaplessHandoffVerifyTimer = setTimeout(() => {
      this._gaplessHandoffVerifyTimer = null;
      void verifyGaplessHandoffPlayback(
        capturedGen,
        queueIndex,
        (track, options) => { if (this._suppressReason === 'gapless_adopt') this._clearAdvanceSuppression('gapless-verify-replay'); return this._playTrack(track, options); },
      );
    }, GAPLESS_HANDOFF_VERIFY_DELAY_MS);
  }

  private _resolveActiveZone() {
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    if (!renderer) return null;
    return store.discovery?.activeZone?.id === renderer.id
      ? store.discovery.activeZone
      : store.discovery?.discoveredZones?.find((z) => z.id === renderer.id) ?? null;
  }

  /** Next queue position after current track, respecting repeat mode. Returns -1 at end. */
  private _nextQueuePosition(
    queueIndex: number,
    queueLength: number,
    repeatMode: string,
  ): number {
    const next = queueIndex + 1;
    if (next < queueLength) return next;
    if (repeatMode === 'all' && queueLength > 0) return 0;
    return -1;
  }

  /** True when repeat is off and the current index is the final queue slot. */
  private _logQueueEndCheck(payload: {
    radioActive: boolean;
    buildStatus: string | null;
    registryUnplayed: number;
    localQueueRemaining: number;
    verdict: string;
  }): void {
    diagnosticLog('info', 'js:radio', 'queue_end_check', payload);
  }

  private _isNaturalQueueEnd(
    store: ReturnType<typeof useMaestroStore.getState> = useMaestroStore.getState(),
  ): boolean {
    if (store.repeatMode === 'one' || store.repeatMode === 'all') return false;
    if (store.queue.length === 0) {
      const radioActive = isRadioQueueSession();
      const registryUnplayed = radioActive ? countRadioRegistryUnplayedBeyondQueue() : 0;
      const shouldDeferRadio = shouldDeferRadioQueueTerminal();
      this._logQueueEndCheck({
        radioActive,
        buildStatus: radioActive ? getRadioSessionBuildStatus() : null,
        registryUnplayed,
        localQueueRemaining: 0,
        verdict: shouldDeferRadio ? 'defer_radio' : 'finished_empty',
      });
      if (shouldDeferRadio) return false;
      return true;
    }
    const atEnd = store.queueIndex >= store.queue.length - 1;
    if (!atEnd) return false;

    const localQueueRemaining = Math.max(0, store.queue.length - store.queueIndex - 1);
    const radioActive = isRadioQueueSession();
    const buildStatus = radioActive ? getRadioSessionBuildStatus() : null;
    const registryUnplayed = radioActive ? countRadioRegistryUnplayedBeyondQueue() : 0;
    const shouldDeferRadio = shouldDeferRadioQueueTerminal();

    this._logQueueEndCheck({
      radioActive,
      buildStatus,
      registryUnplayed,
      localQueueRemaining,
      verdict: shouldDeferRadio ? 'defer_radio' : 'finished',
    });

    if (shouldDeferRadio) return false;
    return true;
  }

  /**
   * Radio-aware terminal guard for _advance paths — never finalize while arc
   * tracks are still pending enqueue or the build is active.
   */
  private async _guardRadioBeforeQueueTerminal(terminalReason: string): Promise<boolean> {
    if (!shouldDeferRadioQueueTerminal()) return false;

    const store = useMaestroStore.getState();
    const radioActive = isRadioQueueSession();
    this._logQueueEndCheck({
      radioActive,
      buildStatus: radioActive ? getRadioSessionBuildStatus() : null,
      registryUnplayed: radioActive ? countRadioRegistryUnplayedBeyondQueue() : 0,
      localQueueRemaining: store.queue.length === 0
        ? 0
        : Math.max(0, store.queue.length - store.queueIndex - 1),
      verdict: 'defer_radio',
    });

    const handled = await this._handleEllaRadioAtQueueEnd('queue-advance');
    if (handled) return true;

    if (shouldDeferRadioQueueTerminal()) {
      const {
        enterRadioWaiting,
        beginRadioQueueEndWait,
        armRadioConnectorHold,
        obligateRadioFeedNow,
      } = await import('../radio/ellaRadioSessionState');
      beginRadioQueueEndWait();
      armRadioConnectorHold();
      await obligateRadioFeedNow(terminalReason);
      enterRadioWaiting();
      this._clearQueueTerminalLatch();
      const refreshed = useMaestroStore.getState();
      if (refreshed.playbackState !== 'buffering') {
        refreshed.setPlaybackState('buffering');
      }
      console.log('[QueueController] radio terminal deferred', { terminalReason });
      return true;
    }
    return false;
  }

  /** End-of-queue terminal: stop renderer, invalidate timers, show ended state — no replay. */
  private async _finalizeQueueTerminal(reason: string): Promise<void> {
    const store = useMaestroStore.getState();
    if (this._queueTerminalHandled && store.playbackState === 'stopped') {
      return;
    }

    this._queueTerminalHandled = true;
    bumpAdvanceGeneration();
    cancelRemoteAdvanceTimer();
    this._clearGaplessHandoffVerify();
    this._clearSetNextHandoffWatchdog();
    clearGaplessPrefetchIndex();
    clearSetNextFillConfirmed();
    this._clearPrefetchedStream();

    console.log('[QueueController] Playlist finished — stopping playback', { reason });
    this._logQueueStateIfChanged(0, store.queue.length, 'end');
    if (store.playbackState !== 'stopped') {
      store.setPlaybackState('stopped');
    }

    // #11258: every network renderer, not only timer-based — upmpdcli replays its last URI.
    if (store.activeConnectorRenderer && _isRemoteRenderer()) {
      try {
        const { stop: transportStop } = await import('./TransportCommand');
        await transportStop(undefined, { caller: 'queue_terminal_network_renderer', userInitiated: false });
      } catch {
        /* best-effort — prevent the renderer from looping its last URI */
      }
    }

    clearQueueOwnershipAcceptanceLatch('queue_terminal');
    TransportState.releaseTransportOwner({ force: true });
  }

  /**
   * SetNext fill was confirmed for the upcoming track — defer playUri at boundary and
   * wait for renderer position events to adopt the transition.
   */
  private _shouldDeferToSetNextHandoff(): boolean {
    if (!isGaplessSetNextEnabled()) return false;

    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const zone = this._resolveActiveZone();
    if (!renderer || !rendererSupportsSetNext(renderer, zone)) return false;

    const nextQueuePos = this._nextQueuePosition(
      store.queueIndex,
      store.queue.length,
      store.repeatMode,
    );
    if (nextQueuePos < 0) return false;
    if (store.repeatMode === 'one') return false;

    if (!isSetNextFillConfirmedForQueueIndex(nextQueuePos)) return false;
    if (getGaplessPrefetchIndex() !== nextQueuePos) return false;

    const { queue, shuffled, shuffleOrder } = store;
    const actualIdx = shuffled && shuffleOrder.length > nextQueuePos
      ? shuffleOrder[nextQueuePos]
      : nextQueuePos;
    const nextTrack = queue[actualIdx]?.track;
    if (nextTrack && isLocalLibraryTrack(nextTrack) && !nextTrack.file_path) {
      return false;
    }

    return true;
  }

  private _armSetNextHandoffWatchdog(capturedGen: number): void {
    this._clearSetNextHandoffWatchdog();
    this._setNextHandoffWatchdog = setTimeout(async () => {
      this._setNextHandoffWatchdog = null;
      if (capturedGen !== getAdvanceGeneration()) return;
      if (isRendererGaplessAdvanceRecent(3000)) return;

      const store = useMaestroStore.getState();
      const prefetchIdx = getGaplessPrefetchIndex();
      if (prefetchIdx >= 0 && store.queueIndex > prefetchIdx) return;

      console.log(
        '[QueueController] SetNext handoff watchdog — renderer did not advance, falling back to playUri',
      );
      clearGaplessPrefetchIndex();
      clearSetNextFillConfirmed();
      this._prebufferedForQueueIndex = -1;
      await this.advanceTrackEnd();
    }, QueueController.SETNEXT_HANDOFF_WATCHDOG_MS);
  }

  /**
   * Timer-based SetNext renderers have no position events — wall-clock track-end is
   * the handoff signal when fill was confirmed. Adopt queue sync without playUri.
   */
  private _completeTimerBasedSetNextHandoff(): boolean {
    if (!this._shouldDeferToSetNextHandoff()) return false;

    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const zone = this._resolveActiveZone();
    if (!isGaplessSetNextEnabled()) return false;
    if (!renderer || !rendererSupportsSetNext(renderer, zone)) return false;
    if (!isTimerBasedRenderer(renderer, zone)) return false;

    const prefetchIdx = getGaplessPrefetchIndex();
    if (prefetchIdx < 0) return false;

    console.log(
      '[QueueController] timer track-end — SetNext fill confirmed, adopting gapless transition at queue',
      prefetchIdx,
    );
    this._clearSetNextHandoffWatchdog();
    this.adoptRendererGaplessTransition(prefetchIdx, 0);
    return true;
  }

  private _handleSetNextHandoffAtBoundary(capturedGen: number, context: string): boolean {
    if (!this._shouldDeferToSetNextHandoff()) return false;
    if (this._completeTimerBasedSetNextHandoff()) {
      console.log(`[QueueController] ${context} — timer SetNext handoff complete`);
      return true;
    }
    console.log(`[QueueController] ${context} — SetNext handoff pending, deferring playUri`);
    this._armSetNextHandoffWatchdog(capturedGen);
    return true;
  }

  private _isFailureRecoveryActive(): boolean {
    return this._failureRecoveryOwner !== 'none';
  }

  /** Claim sole ownership of failure recovery; suppresses competing advance paths. */
  private _claimFailureRecovery(owner: Exclude<FailureRecoveryOwner, 'none'>): boolean {
    if (this._failureRecoveryOwner !== 'none') {
      console.log('[QueueController] failure recovery already owned by', this._failureRecoveryOwner);
      return false;
    }
    this._failureRecoveryOwner = owner;
    this._abortGaplessPrefetch();
    if (this._failureRecoveryReleaseTimer) {
      clearTimeout(this._failureRecoveryReleaseTimer);
    }
    this._failureRecoveryReleaseTimer = setTimeout(() => {
      if (this._failureRecoveryOwner === owner) {
        this._failureRecoveryOwner = 'none';
      }
      this._failureRecoveryReleaseTimer = null;
    }, 12_000);
    return true;
  }

  private _releaseFailureRecovery(): void {
    this._failureRecoveryOwner = 'none';
    if (this._failureRecoveryReleaseTimer) {
      clearTimeout(this._failureRecoveryReleaseTimer);
      this._failureRecoveryReleaseTimer = null;
    }
  }

  /**
   * Authoritative track-end from Connector timer monitor (MU2/Ideon/AirLens-class).
   * Bypasses position-gated STOPPED handling and useRemoteAdvanceTimer latency.
   */
  async handleRendererTrackEnded(rendererId: string, source = 'timer'): Promise<void> {
    if (!shouldHonorQueueTrackEndAdvance()) {
      console.log('[QueueController] renderer-track-ended ignored — transport owner is not queue');
      return;
    }
    if (this._queueTerminalHandled) {
      console.log('[QueueController] renderer-track-ended ignored — queue terminal handled');
      return;
    }
    if (this._handleAdvanceSuppressed('renderer-track-ended', 'renderer-track-ended-suppressed', {
      generation: getAdvanceGeneration(),
      queueIndex: useMaestroStore.getState().queueIndex,
      rendererId,
    })) {
      return;
    }
    if (isTestPlaybackMode()) return;

    const store = useMaestroStore.getState();
    const activeId = store.activeConnectorRenderer?.id;
    if (!activeId || rendererId !== activeId) return;

    const capturedGen = getAdvanceGeneration();
    const trackId = store.currentTrack?.id;

    const dedupeKey = `${rendererId}:${trackId || 'unknown'}:${capturedGen}`;
    const dedupeNow = Date.now();
    if (
      this._lastTrackEndDedupeKey === dedupeKey
      && dedupeNow - this._lastTrackEndDedupeAt < QueueController.TRACK_END_DEDUPE_MS
    ) {
      console.log('[QueueController] renderer-track-ended deduped', { dedupeKey });
      return;
    }
    this._lastTrackEndDedupeKey = dedupeKey;
    this._lastTrackEndDedupeAt = dedupeNow;

    console.log('[QueueController] renderer-track-ended', {
      rendererId,
      source,
      trackId,
      generation: capturedGen,
    });

    cancelRemoteAdvanceTimer();

    if (getSyncHandler().isObserving()) {
      console.log('[QueueController] renderer-track-ended — observer waits for controller sync');
      return;
    }

    if (capturedGen !== getAdvanceGeneration()) {
      console.log('[QueueController] renderer-track-ended — generation stale, skipping');
      return;
    }

    if (!this._guardTimerPathAdvance('renderer-track-ended', rendererId, capturedGen, trackId, {
      bypassWallClockAtGenuineEnd: source === 'stall-reconcile',
      trackEndSource: source,
    })) {
      return;
    }

    if (this._handleSetNextHandoffAtBoundary(capturedGen, 'renderer-track-ended')) {
      return;
    }

    if (this._isNaturalQueueEnd(store)) {
      if (await this._guardRadioBeforeQueueTerminal(`renderer-track-ended:${source}`)) return;
      await this._finalizeQueueTerminal(`renderer-track-ended:${source}`);
      return;
    }

    if (store.playbackState !== 'stopped') {
      store.setPlaybackState('stopped');
    }

    await this._advance('end');
  }

  /**
   * Natural track-end advance (renderer-track-ended, wall-clock fallback, stopped-near-end).
   * Uses _advance('end') — NOT next() — so we do not arm suppressAdvance() and block the
   * authoritative renderer-track-ended path, and diagnostics log reason:end not skip.
   */
  async advanceTrackEnd(options?: { trustExclusiveEngineEnd?: boolean }): Promise<void> {
    if (!shouldHonorQueueTrackEndAdvance()) {
      console.log('[QueueController] advanceTrackEnd ignored — transport owner is not queue');
      return;
    }
    if (this._handleAdvanceSuppressed('advanceTrackEnd', 'advanceTrackEnd-suppressed')) {
      return;
    }
    if (isTestPlaybackMode()) return;

    cancelRemoteAdvanceTimer();

    if (getSyncHandler().isObserving()) {
      console.log('[QueueController] advanceTrackEnd — observer waits for controller sync');
      return;
    }

    const capturedGen = getAdvanceGeneration();
    if (capturedGen !== getAdvanceGeneration()) {
      console.log('[QueueController] advanceTrackEnd — generation stale, skipping');
      return;
    }

    const store = useMaestroStore.getState();
    const trackId = store.currentTrack?.id;
    if (!this._guardTimerPathAdvance('advanceTrackEnd', store.activeConnectorRenderer?.id, capturedGen, trackId, {
      trustExclusiveEngineEnd: options?.trustExclusiveEngineEnd === true,
    })) {
      return;
    }

    if (this._handleSetNextHandoffAtBoundary(capturedGen, 'advanceTrackEnd')) {
      return;
    }

    if (this._isNaturalQueueEnd(store)) {
      if (await this._guardRadioBeforeQueueTerminal('advanceTrackEnd')) return;
      await this._finalizeQueueTerminal('advanceTrackEnd');
      return;
    }

    if (store.playbackState !== 'stopped') {
      store.setPlaybackState('stopped');
    }

    await this._advance('end');
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    let previousState = useMaestroStore.getState().playbackState;
    let previousZoneCount = useMaestroStore.getState().discovery?.discoveredZones?.length || 0;

    this._unsubscribe = useMaestroStore.subscribe((state, prevState) => {
      if (prevState) {
        this._maybeClearSuppressionOnConfirmedPlay(state, prevState);
      }

      const currentState = state.playbackState;
      if (prevState && currentState === prevState.playbackState) {
        return;
      }

      if (currentState === 'playing' && previousState !== 'playing') {
        this._consecutiveFails = 0;
        this._rendererLostAt = 0;
        this._rendererLostId = null;
        const playingTrackId = state.currentTrack?.id;
        if (playingTrackId) {
          this._trackReachedPlayingId = String(playingTrackId);
          this._clearStalledStartWatch();
        }
        if (_isRemoteRenderer()) {
          const az = state.discovery?.activeZone;
          const renderer = state.activeConnectorRenderer;
          markNetworkPlaySucceeded(az?.host, renderer?.name || az?.name);
        }
      }

      // Auto-recovery: when the renderer reappears after being lost, retry playback
      const zoneCount = state.discovery?.discoveredZones?.length || 0;
      if (this._rendererLostAt > 0 && this._rendererLostId && zoneCount > previousZoneCount) {
        const elapsed = Date.now() - this._rendererLostAt;
        if (elapsed < 120_000) { // within 2 minutes of loss
          const found = state.discovery?.discoveredZones?.some(
            (z: any) => z.id === this._rendererLostId
          );
          if (found && currentState === 'paused' && TransportState.isQueueOwner()) {
            console.log('[QueueController] Renderer recovered — auto-resuming playback');
            this._rendererLostAt = 0;
            this._rendererLostId = null;
            this._playCurrentTrack();
          }
        } else {
          this._rendererLostAt = 0;
          this._rendererLostId = null;
        }
      }
      previousZoneCount = zoneCount;

      const wasActive = previousState === 'playing' || previousState === 'loading';
      if (wasActive && currentState === 'stopped') {
        const sincePlayUri = Date.now() - this._lastPlayUriTime;
        const tcRecentMs = 8000;
        const isTransportRecent = TransportCommand.isPlayUriRecent(tcRecentMs);

        console.log(`[QueueController] ${previousState}→stopped detected`,
          'sincePlayUri:', sincePlayUri, 'ms',
          '_lastPlayUriTime:', this._lastPlayUriTime,
          'TransportCommand.isPlayUriRecent:', isTransportRecent,
          'isRemote:', _isRemoteRenderer());

        if (!shouldHonorQueueTrackEndAdvance()) {
          console.log('[QueueController.subscribe] playing→stopped ignored — transport owner is not queue');
          previousState = currentState;
          return;
        }

        if (Date.now() < this._suppressUntil) {
          const remainingMs = Math.max(0, this._suppressUntil - Date.now());
          console.log('[QueueController.subscribe] playing→stopped suppressed', {
            reason: this._suppressReason,
            remainingMs,
          });
          previousState = currentState;
          return;
        }

        if (isIOSNativeApp() && document.visibilityState === 'hidden') {
          console.log('[QueueController.subscribe] playing→stopped suppressed — iOS app is backgrounded');
          previousState = currentState;
          return;
        }

        const isRapidFail = (sincePlayUri < 8000 && this._lastPlayUriTime > 0) || isTransportRecent;
        if (isRapidFail) {
          if (_testPlaybackMode) {
            console.log('[QueueController] playing→stopped during test playback — Devices page handles this');
            previousState = currentState;
            return;
          }
          // Network renderers briefly report STOPPED during URI handoff / gapless
          // transitions — not a stream failure. Timer or renderer-track-ended advances.
          if (_isRemoteRenderer()) {
            console.log('[QueueController.subscribe] rapid playing→stopped on remote renderer — normal handoff, ignoring');
            previousState = currentState;
            return;
          }
          this._handlePlayUriFailed();
          previousState = currentState;
          return;
        }

        if (_isRemoteRenderer()) {
          if (this._handleSetNextHandoffAtBoundary(getAdvanceGeneration(), 'playing→stopped')) {
            previousState = currentState;
            return;
          }
          if (state.activeSource !== 'radio') {
            console.log('[QueueController.subscribe] playing→stopped on remote renderer — timer handles advance');
            previousState = currentState;
            return;
          }
          console.log('[QueueController.subscribe] playing→stopped on radio remote — guarded advance path');
          void this.advanceTrackEnd();
          previousState = currentState;
          return;
        }

        // Playback never started (resolve/playUri never completed) — not a natural track end.
        if (previousState === 'loading' && this._lastPlayUriTime === 0 && !isTransportRecent) {
          console.log('[QueueController] loading→stopped before playback started — ignoring end-of-track advance');
          previousState = currentState;
          return;
        }

        setTimeout(() => { this._advance('end'); }, 0);
      }
      previousState = currentState;
    });
  }

  destroy() {
    this._unsubscribe?.();
    if (this._suppressedEndRecheck) {
      clearTimeout(this._suppressedEndRecheck.timer);
      this._suppressedEndRecheck = null;
    }
    this._initialized = false;
  }

  /** Replay the current queue track — used by auto-recovery after renderer loss */
  private _playCurrentTrack() {
    const store = useMaestroStore.getState();
    const idx = store.queueIndex;
    if (idx < 0 || idx >= store.queue.length) return;
    const item = store.queue[idx];
    if (!item) return;
    this._playTrack(item.track, { intent: 'user' });
  }

  private _handlePlayUriFailed() {
    if (this._failAdvancing) return;

    if (shouldRetainQueueTransportOwnership()) {
      console.warn('[QueueController] playUri failed suppressed — queue ownership latched on acceptance evidence (#598 G)');
      return;
    }

    const isUserIntent = this._playIntent === 'user';
    const recoveryOwner: Exclude<FailureRecoveryOwner, 'none'> = isUserIntent ? 'user-pause' : 'queue-skip';
    if (!this._claimFailureRecovery(recoveryOwner)) return;

    this._failAdvancing = true;

    const now = Date.now();
    if (now - this._consecutiveFailWindowStart > 30000) {
      this._consecutiveFails = 0;
      this._consecutiveFailWindowStart = now;
    }
    this._consecutiveFails++;

    const store = useMaestroStore.getState();
    const zoneName = store.discovery?.activeZone?.name || store.activeConnectorRenderer?.name || 'the renderer';
    const trackTitle = store.currentTrack?.title || 'This track';
    const isLocal = store.currentTrack?.source === 'local_library';
    const failReason = this._consecutiveFails >= 3
      ? 'consecutive_failures'
      : this._lastPlayUriFormatId === 6 && store.currentStreamFormatId === 27
        ? 'format_downgrade'
        : isLocal
          ? 'local_playback_error'
          : 'streaming_invalid_file';

    diagnosticLog(
      'error',
      'playback',
      `Track failed: ${trackTitle} — ${failReason}`,
      {
        trackId: store.currentTrack?.id,
        renderer: zoneName,
        sincePlayUri: now - this._lastPlayUriTime,
        reason: failReason,
        playIntent: this._playIntent,
      },
    );

    console.warn('[QueueController] playUri failed — stopped shortly after playUri.', {
      sincePlayUri: now - this._lastPlayUriTime,
      trackTitle,
      zoneName,
      formatId: this._lastPlayUriFormatId,
      consecutiveFails: this._consecutiveFails,
      isLocal,
      playIntent: this._playIntent,
    });

    if (isUserIntent) {
      markTransportPlayFailure(store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id);
      showUserPlayRetryToast(
        trackTitle,
        zoneName,
        null,
        store.currentTrack,
        store.currentStreamFormatId ?? this._lastPlayUriFormatId,
        store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id,
      );
      this._failAdvancing = false;
      this._releaseFailureRecovery();
      return;
    }

    if (this._consecutiveFails >= 3) {
      showImperativeToast(
        isLocal
          ? `Multiple tracks aren't playing. The file format may not be supported by "${zoneName}". Try playing on a different device.`
          : 'Multiple tracks on this album aren\'t playing. The streaming service is experiencing a temporary issue. Try a different album, or try again later.',
        10000,
      );
      store.setPlaybackState('paused');
      this._failAdvancing = false;
      this._releaseFailureRecovery();
      return;
    }

    if (this._lastPlayUriFormatId === 6 && store.currentStreamFormatId === 27) {
      showImperativeToast('Playing at CD quality — hi-res temporarily unavailable', 10000);
    } else if (isLocal) {
      const fmt = String(store.currentTrack?.format || '').toUpperCase();
      const isDsd = fmt === 'DSF' || fmt === 'DFF';
      showImperativeToast(
        isDsd
          ? `Couldn't play "${trackTitle}" — DSD files require a compatible renderer. Try playing on a DSD-capable device.`
          : `Couldn't play "${trackTitle}" on "${zoneName}". The file may be in an unsupported format.`,
        10000,
      );
    } else {
      showImperativeToast(
        `Couldn't play "${trackTitle}" — the streaming service returned an invalid file. This is usually a temporary issue.`,
        10000,
      );
    }

    const currentQueueIndex = store.queueIndex;
    const queueLen = store.queue.length;
    console.log('[QueueController] Auto-advancing to next track. queueIndex:', currentQueueIndex, 'queueLength:', queueLen);

    setTimeout(async () => {
      try {
        await this._advance('failure');
        const newIndex = useMaestroStore.getState().queueIndex;
        console.log('[QueueController] Advance complete. New queueIndex:', newIndex, 'was:', currentQueueIndex);
      } catch (err) {
        console.error('[QueueController] Advance threw:', err);
      }
      this._failAdvancing = false;
      this._releaseFailureRecovery();
    }, 500);
  }

  resetFailCounter() {
    this._consecutiveFails = 0;
  }

  private _capturePlaybackSnapshot(): PriorPlaybackSnapshot {
    const s = useMaestroStore.getState();
    return {
      currentTrack: s.currentTrack,
      currentStreamUrl: s.currentStreamUrl,
      currentStreamFormatId: s.currentStreamFormatId,
      alternateStreamUrl: s.alternateStreamUrl,
      alternateStreamFormatId: s.alternateStreamFormatId,
      queueIndex: s.queueIndex,
      position: s.position,
      duration: s.duration,
      playbackState: s.playbackState,
      rendererHasStream: s.rendererHasStream,
    };
  }

  private _clearPlayTrackInflight(): void {
    this._isPlayTrackInFlight = false; this._inFlightTrackId = null; _lastPlayTrackCompletedAt = 0;
  }

  /** Restore transport URI + track so play() can resume after a failed selection. */
  private _restorePlaybackSnapshot(snap: PriorPlaybackSnapshot | null): void {
    if (!snap?.currentTrack && !snap?.currentStreamUrl) return;
    const s = useMaestroStore.getState();
    s.setCurrentTrack(snap.currentTrack);
    s.setCurrentStreamUrl(snap.currentStreamUrl);
    s.setCurrentStreamFormatId(snap.currentStreamFormatId);
    s.setAlternateStreamUrl(snap.alternateStreamUrl);
    s.setAlternateStreamFormatId(snap.alternateStreamFormatId);
    s.setRendererHasStream(snap.rendererHasStream);
    s.updateTime(snap.position, snap.duration);
    if (snap.currentTrack?.id != null) {
      const idx = s.queue.findIndex((item) => item.track?.id === snap.currentTrack.id);
      if (idx >= 0) s.setQueueIndex(idx);
    }
    const resumeState = snap.playbackState === 'loading' ? 'paused' : snap.playbackState;
    s.setPlaybackState(resumeState);
    console.log('[QueueController] Restored prior playback after failed track selection');
  }

  private _clearPlayTrackCooldownForSkip(): void {
    _lastPlayTrackCompletedAt = 0;
  }

  private async _handleEllaRadioAtQueueEnd(
    intent: 'queue-advance' | 'user',
  ): Promise<boolean> {
    if (!isRadioQueueSession()) return false;

    const {
      isRadioExpectingMore: radioExpectingMore,
      enterRadioWaiting,
      clearRadioWaiting,
      waitForRadioBuildQueueGrowth,
      armRadioConnectorHold,
      beginRadioQueueEndWait,
      tryRadioAutoResume,
    } = await import('../radio/ellaRadioSessionState');

    const { flushEllaRadioPendingEnqueue } = await import('../radio/ellaRadioBuildPoll');
    await flushEllaRadioPendingEnqueue();
    const afterFlush = useMaestroStore.getState();
    if (afterFlush.queueIndex + 1 < afterFlush.queue.length) {
      this._clearQueueTerminalLatch();
      if (tryRadioAutoResume('queue_end_flush')) {
        return true;
      }
    }

    if (radioExpectingMore()) {
      beginRadioQueueEndWait();
      armRadioConnectorHold();
      const gotBuildTracks = await waitForRadioBuildQueueGrowth();
      const refreshed = useMaestroStore.getState();
      if (gotBuildTracks && refreshed.queueIndex + 1 < refreshed.queue.length) {
        this._clearQueueTerminalLatch();
        if (tryRadioAutoResume('queue_end_build_growth')) {
          return true;
        }
      }
      if (radioExpectingMore()) {
        enterRadioWaiting();
        this._clearQueueTerminalLatch();
        if (refreshed.playbackState !== 'buffering') {
          refreshed.setPlaybackState('buffering');
        }
        return true;
      }
    }

    try {
      const { waitForEllaRadioRefill } = await import('../radio/radioRefill');
      const { isRadioArcIngestionPending } = await import('../radio/ellaRadioSessionState');
      if (!isRadioArcIngestionPending()) {
        const gotMore = await waitForEllaRadioRefill();
        const afterRefill = useMaestroStore.getState();
        if (gotMore && afterRefill.queueIndex + 1 < afterRefill.queue.length) {
          this._clearQueueTerminalLatch();
          if (tryRadioAutoResume('queue_end_refill')) {
            return true;
          }
        }
      }
    } catch (e) {
      console.warn('[QueueController] Ella radio end-of-queue refill wait failed', e);
    }

    return false;
  }

  private async _advance(reason: 'end' | 'failure' | 'skip' = 'end') {
    if (!shouldHonorQueueTrackEndAdvance()) {
      console.log('[QueueController] _advance blocked — transport owner is not queue', { reason });
      return;
    }
    if (this._advancing) {
      console.warn('[QueueController] _advance reentrant call suppressed', { reason });
      return;
    }
    this._advancing = true;
    try {
    const store = useMaestroStore.getState();
    if (store.queue.length === 0) {
      if (await this._guardRadioBeforeQueueTerminal('advance-empty-queue')) return;
      await this._finalizeQueueTerminal('advance-empty-queue');
      return;
    }
    this._clearQueueTerminalLatch();

    const fromTrack = store.queue[store.queueIndex]?.track;
    const fromId = fromTrack?.id;
    const fromTitle = fromTrack?.title || '';

    const { queue, queueIndex, repeatMode, shuffled, shuffleOrder } = store;

    if (repeatMode === 'one') {
      const idx = shuffled && shuffleOrder.length > queueIndex ? shuffleOrder[queueIndex] : queueIndex;
      const item = queue[idx];
      _lastPlayTrackCompletedAt = 0;
      if (item?.track) await this._playTrack(item.track, { intent: 'queue-advance' });
      return;
    }

    const nextIndex = queueIndex + 1;

    if (nextIndex >= queue.length) {
      if (repeatMode === 'all') {
        store.setQueueIndex(0);
        const idx = shuffled && shuffleOrder.length > 0 ? shuffleOrder[0] : 0;
        const item = queue[idx];
        const toT = item?.track;
        diagnosticLog(
          'info',
          'playback',
          `Auto-advance from "${fromTitle}" to "${toT?.title || ''}"`,
          { fromTrackId: fromId, toTrackId: toT?.id, reason },
        );
        // Metadata update moved to _playTrack entry to prevent race with async operations.
        if (item?.track) await this._playTrack(item.track, { intent: 'queue-advance' });
      } else {
        if (await this._guardRadioBeforeQueueTerminal('queue-advance-end')) return;
        await this._finalizeQueueTerminal('queue-advance-end');
      }
      return;
    }

    store.setQueueIndex(nextIndex);
    clearQueueOwnershipAcceptanceLatch('queue_advance');
    const idx = shuffled && shuffleOrder.length > nextIndex ? shuffleOrder[nextIndex] : nextIndex;
    const item = queue[idx];
    const toTrack = item?.track;
    diagnosticLog(
      'info',
      'playback',
      `Auto-advance from "${fromTitle}" to "${toTrack?.title || ''}"`,
      { fromTrackId: fromId, toTrackId: toTrack?.id, reason },
    );
    _lastPlayTrackCompletedAt = 0;
    // Metadata update moved to _playTrack entry to prevent race with async operations.
    if (item?.track) await this._playTrack(item.track, { intent: 'queue-advance' });
    } finally {
      this._advancing = false;
    }
  }

  private async _playTrack(
    track: any,
    options?: { intent?: PlayIntent; proxyUnreachableAttempt?: number },
  ) {
    const intent = options?.intent ?? 'user';
    this._playIntent = intent;
    if (intent === 'user') {
      clearExclusiveErrorOnUserPlayStart();
    }

    const requestedId = track?.id != null ? String(track.id) : null;
    if (this._isPlayTrackInFlight) {
      if (this._isFailureRecoveryActive()) {
        console.warn(
          '[QueueController] _playTrack blocked during failure recovery',
          'owner:', this._failureRecoveryOwner,
          'requested:', requestedId,
        );
        return;
      }
      if (
        (intent === 'queue-advance' || intent === 'user')
        && requestedId
        && this._inFlightTrackId
        && requestedId !== this._inFlightTrackId
      ) {
        console.warn(
          '[QueueController] superseding in-flight play',
          'intent:', intent,
          'inFlight:', this._inFlightTrackId,
          'requested:', requestedId,
        );
        this._isPlayTrackInFlight = false;
        this._inFlightTrackId = null;
      } else {
        console.warn('[QueueController] _playTrack in-flight guard hit — blocked duplicate', 'trackId:', track?.id);
        diagnosticLog('info', 'playback', 'in-flight guard hit', {
          track_id: track?.id,
        });
        return;
      }
    }
    const nowMs = Date.now();
    const cooldownRemaining = 3000 - (nowMs - _lastPlayTrackCompletedAt);
    if (_lastPlayTrackCompletedAt > 0 && cooldownRemaining > 0) {
      console.warn('[QC] _playTrack COOLDOWN — called within 3s of last completion, ignoring. track:', track?.title);
      diagnosticLog('info', 'playback', 'cooldown guard hit', {
        track_id: track?.id,
        ms_remaining: cooldownRemaining,
      });
      return;
    }
    if (shouldBlockDuplicatePlayUri(track?.id, options?.intent ?? this._playIntent)) {
      console.warn('[QC] _playTrack blocked — PLAYING-PENDING for current track', track?.id);
      diagnosticLog('info', 'playback', 'play_uri_pending_block', {
        track_id: track?.id,
        intent: options?.intent ?? this._playIntent,
      });
      return;
    }
    this._isPlayTrackInFlight = true;
    this._inFlightTrackId = track?.id != null ? String(track.id) : null;
    const playToken = beginPlayTrackToken();
    bumpAdvanceGeneration();
    noteTrackSelectionStarted(track?.id);
    this._clearStalledStartWatch();
    this._stalledStartRetriedTrackId = null;
    this._trackReachedPlayingId = null;
    this._clearPrefetchedStream();
    clearSetNextFillConfirmed();
    this._clearSetNextHandoffWatchdog();
    this._clearGaplessHandoffVerify();
    markUserPlaybackStarted();
    clearQueueOwnershipLatchForNewTrack(track?.id);
    TransportState.claimTransportOwner('queue');
    this._clearQueueTerminalLatch();

    const storeAtEntry = useMaestroStore.getState();
    // Update metadata synchronously BEFORE any async operations to prevent stale UI.
    // This ensures both click and auto-advance paths update identically at entry.
    const initialDur = resolveQueueTrackDuration(track);
    track = applyResolvedTrackDuration(track, initialDur);
    storeAtEntry.setCurrentTrack(track);
    storeAtEntry.updateTime(0, initialDur ?? 0);

    const rendererAtStart = storeAtEntry.activeConnectorRenderer;
    const localOnlyLocked = !rendererAtStart && !_isRemoteRenderer();
    diagnosticLog('info', 'playback', '_playTrack entry', {
      queue_index: storeAtEntry.queueIndex,
      track_id: track?.id,
      has_file_path: !!track?.file_path,
    });

    // ---------------------------------------------------------------
    // Defensive cross-surface teardown.
    //
    // Scenario: user is playing Ella Radio or Qobuz (either via the
    // browser's HTML5 audio element on "This Computer", or via a network
    // renderer) and then hits a library track. Without an explicit
    // teardown, whichever surface was playing before can keep running
    // alongside the new track — classic "two things playing at once".
    //
    // We aggressively pause/destroy every non-library surface before
    // dispatching the new track. Library tracks themselves are safe to
    // swap in-place via WebAudioRenderer (source swap) or SetAVTransportURI
    // (UPnP replace), so this only fires for foreign surfaces.
    // ---------------------------------------------------------------
    try {
      if (storeAtEntry.activeSource === 'radio') {
        try {
          const { stopRadio } = await import('../radio/useRadioPlayback');
          stopRadio();
        } catch { /* best effort */ }
      }

      // 2) Flip the store's active source so PlaybackSnapshot readers
      //    (MiniPlayer, iOS Now Playing, queue sync) stop reading from
      //    radioStore and start reading from maestroStore.
      try {
        const s = useMaestroStore.getState();
        if (s.activeSource !== 'library') s.setActiveSource('library');
      } catch {}

      // 3) Pause the browser's local audio element. If we're about to
      //    route to a network renderer, this kills any Qobuz stream the
      //    browser was playing. If we're about to route locally,
      //    WebAudioRenderer will immediately set a new src and play.
      try {
        TransportCommand.pauseLocalAudioElement();
      } catch { /* best effort */ }
    } catch (e) {
      console.warn('[QC] cross-surface teardown failed (non-fatal):', e);
    }

    try {
      const store = useMaestroStore.getState();
      diagnosticLog(
        'info',
        'playback',
        `Track selected: ${track?.title || '?'} by ${track?.artist || '?'}`,
        {
          trackId: track?.id,
          source: track?.source,
          queuePosition: store.queueIndex,
          queueLength: store.queue.length,
        },
      );
      const mz = store.multizone;
      const isMultizone = mz.enabled && mz.groupedZones.length >= 2;
      let renderer = rendererAtStart;
      syncRendererConnectivityFromZones();
      const conn = store.rendererConnectivityState;
      const zones = store.discovery?.discoveredZones || [];
      const az = store.discovery?.activeZone as any;

      const pinnedRenderer = renderer
        || (isRemoteRendererZone(az) ? { id: az.id, name: az.name || az.id } : null);

      if (pinnedRenderer && zones.length > 0 && !zones.some((z: any) => z.id === pinnedRenderer.id)) {
        if (conn === 'reconnecting') {
          console.log('[QC] Play queued while reconnecting to', az?.name || pinnedRenderer.id);
          store.setPendingPlayWhileReconnecting({ kind: 'track', track });
          return;
        }
        if (conn === 'unreachable') {
          const deviceName = az?.name || pinnedRenderer.name || 'Device';
          console.log('[QC] Unreachable renderer — prompting for fallback:', deviceName);
          store.setPlaybackState('paused');
          store.setUnreachablePlayPrompt({
            deviceName,
            onConfirm: () => {
              store.setUnreachablePlayPrompt(null);
              store.setActiveZone({ id: 'this_device', name: 'System Audio', type: 'local' } as any);
              store.setActiveConnectorRenderer(null);
              store.setRendererConnectivity('live', null);
              void this._playTrack(track, { intent: 'user' });
            },
            onCancel: () => {
              store.setUnreachablePlayPrompt(null);
            },
          });
          return;
        }
      }

      // Recover renderer from activeZone when activeConnectorRenderer is null.
      // Skip when this play was locked to local at entry — discovery restore
      // must not redirect an in-flight local/exclusive play to a network zone.
      if (!renderer && !localOnlyLocked && !isMultizone) {
        const az = store.discovery?.activeZone as any;
        if (az && _isRemoteRenderer()) {
          renderer = connectorRendererFromZone(az);
          store.setActiveConnectorRenderer(renderer);
          console.log('[QC] Recovered renderer from activeZone:', renderer.name);
        }
      }

      console.log('[QC] _playTrack:', track?.title, '→', renderer?.name || 'local');

      // If this track was pre-buffered via SetNextAVTransportURI, the renderer
      // already auto-transitioned to it (gapless). Just update the store — do NOT
      // send another SetAVTransportURI which would restart the track from 0:00.
      // User skip/previous is an explicit command — always issue playUri (never skip).
      if (
        intent !== 'user'
        && renderer
        && !isMultizone
        && !isLocalLibraryTrack(track)
        && this._prebufferedForQueueIndex === store.queueIndex
        && isRendererGaplessAdvanceRecent()
      ) {
        console.log('[QC] Track was pre-buffered via NextURI — renderer auto-transitioned, skipping playUri');
        diagnosticLog(
          'info',
          'playback',
          `Track selected: ${track?.title || '?'} by ${track?.artist || '?'} (gapless)`,
          {
            trackId: track?.id,
            source: track?.source,
            queuePosition: store.queueIndex,
            queueLength: store.queue.length,
          },
        );
        this._prebufferedForQueueIndex = -1;
        clearGaplessPrefetchIndex();
        // Metadata already set at _playTrack entry; just set playing state.
        store.setPlaybackState('playing');
        this._recordPlayUriDispatch();
        this._consecutiveSkips = 0;
        queueMicrotask(() => {
          if (!this._isFailureRecoveryActive()) this._prebufferNextTrack();
        });
        return;
      }

      const priorPlayback = this._capturePlaybackSnapshot();
      store.clearStreamCache();
      // Metadata already set at _playTrack entry; just set loading state.
      store.setPlaybackState('loading');

      {
        const totalLoaded = store.queue.length;
        const remaining = Math.max(0, totalLoaded - store.queueIndex - 1);
        if (remaining <= 2) {
          this._logQueueStateIfChanged(remaining, totalLoaded, 'play_track');
        }
      }

      // Detect local library tracks — never send local:track:* ids to Qobuz.
      const trackKey = String(track?.id || track?.trackId || '');
      const looksLocal = isLocalLibraryTrack(track) || trackKey.startsWith('local:track:');
      const hasFilePath = !!track?.file_path;
      const hasQobuzId = !!(track?.qobuzTrackId || track?.serviceTrackId);

      if (looksLocal || (hasFilePath && !hasQobuzId)) {
        if (looksLocal) {
          track = { ...track, source: 'local_library' };
        } else if (hasFilePath) {
          console.log('[QueueController] Local track detected by file_path fallback (source field missing/wrong):', track.source, '→ routing to local playback');
          track = { ...track, source: 'local_library' };
        }
        track = await this._hydrateLocalTrackFromLibrary(track);
        if (track?.file_path) {
          store.setCurrentTrack(track);
        }
        if (!track?.file_path) {
          console.warn('[QueueController] Local track missing file_path:', trackKey || track?.title);
          showImperativeToast(
            `"${track?.title || 'This track'}" can't play — file path missing. Re-add it from your local library.`,
            8000,
          );
          await handleQueuePlayFailure(this, priorPlayback, { suppressSkipToast: true });
          return;
        }
        await this._playLocalLibraryTrack(track, {
          isMultizone,
          renderer,
          localOnlyLocked,
          proxyUnreachableAttempt: options?.proxyUnreachableAttempt,
        });
        this._consecutiveSkips = 0;
        return;
      }

      // Streaming tracks — clear local USB output-path UI (Qobuz routing unchanged).
      useMaestroStore.getState().setUsbOutputPath(null);
      useMaestroStore.getState().setUsbOutputFallbackNotice(null);

      const qid = track.qobuzTrackId || track.serviceTrackId || track.id;
      if (isLocalTrackId(qid)) {
        console.warn('[QueueController] Refusing Qobuz resolution for local track id:', qid);
        await handleQueuePlayFailure(this, priorPlayback);
        return;
      }
      if (!qid) {
        console.warn('[QueueController] No track ID for stream URL resolution');
        await handleQueuePlayFailure(this, priorPlayback);
        return;
      }

      const formatId = isMultizone
        ? getMultizoneFormatId()
        : getFormatIdForRenderer(renderer);
      let formatAttempt: FormatAttempt | null = null; // this play's own attempt (superseded / restart handling)
      try {
        const prefetched =
          !isMultizone
          && this._prefetchedStream
          && this._prefetchedStream.queueIndex === store.queueIndex
          && this._prefetchedStream.trackId === String(qid) && formatWithinLearnedCeiling(renderer, this._prefetchedStream.formatId)
            ? this._prefetchedStream
            : null;

        const data = prefetched
          ? {
              url: prefetched.url,
              formatId: prefetched.formatId,
              format: prefetched.format,
              samplingRate: prefetched.samplingRate,
              bitDepth: prefetched.bitDepth,
            }
          : await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
        if (dropSupersededPlay(playToken, track?.id, 'stream_url_resolved')) return;

        if (prefetched) {
          console.log('[QC] Using pre-resolved stream URL for track', qid);
          this._clearPrefetchedStream();
        }

        const url = data?.url || '';
        if (!url) {
          console.warn('[QueueController] No stream URL returned for:', qid);
          await runQueueStreamResolutionFailureFor(this, priorPlayback);
          return;
        }

        const resolvedDur = resolveQueueTrackDuration(track, data);
        if (resolvedDur != null) {
          track = applyResolvedTrackDuration(track, resolvedDur);
          store.setCurrentTrack(track);
          store.updateTime(store.position || 0, resolvedDur);
          backfillQueueRowDuration(track?.id, resolvedDur);
        }

        console.log('[Qobuz] stream URL fetched: trackId=' + qid + ' format=' + (data?.formatId || formatId));

        const resolvedFormatForLog = data?.formatId || formatId;
        diagnosticLog(
          'info',
          'playback',
          `Stream URL resolved: format ${resolvedFormatForLog}, ${url.length} chars`,
          {
            format: resolvedFormatForLog,
            urlLength: url.length,
            trackId: qid,
          },
        );

        const resolvedFormat = data?.formatId || formatId;
        const srcRate = data?.samplingRate || 0;
        const srcDepth = data?.bitDepth || 0;

        const decision = routeStream(url, srcRate, srcDepth, resolvedFormat);

        store.setCurrentStreamUrl(decision.effectiveUrl);
        store.setCurrentStreamFormatId(resolvedFormat);

        const prefetchAlternateFormatAfterPlay = () => {
          if (isMultizone) return;
          const altFormatId = resolvedFormat === 27 ? 6 : 27;
          const prefetchQid = String(qid);
          useQobuzStore.getState().getStreamUrl(prefetchQid, altFormatId).then(altData => {
            const s = useMaestroStore.getState();
            const curId = String(s.currentTrack?.qobuzTrackId || s.currentTrack?.serviceTrackId || s.currentTrack?.id || '');
            if (curId && curId !== prefetchQid) {
              console.warn('[QC] prefetch alternate skipped — current track changed', { prefetchQid, curId });
              return;
            }
            const altUrl = altData?.url || '';
            if (altUrl) {
              s.setAlternateStreamUrl(altUrl);
              s.setAlternateStreamFormatId(altData?.formatId || altFormatId);
            }
          }).catch(() => {});
        };

        // --- Multizone path: send one URL to all zones in parallel ---
        if (isMultizone) {
          diagnosticLog(
            'info',
            'transport',
            `playUri sent to multizone (${store.multizone.groupedZones.length} zones)`,
            {
              renderer: 'multizone',
              rendererType: 'multizone',
              trackId: track?.id,
              urlLength: (decision.effectiveUrl || '').length,
            },
          );
          await this._playTrackMultizone(decision.effectiveUrl, resolvedFormat, {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: srcDepth,
            sampleRate: srcRate,
          });
          this._consecutiveSkips = 0;
          return;
        }

        // --- Single-zone path ---
        const azStream = store.discovery?.activeZone;
        const localNativeZone = isTauriLocalNativeZone(azStream);
        const routeExclusive = await TransportCommand.shouldUseExclusive();
        if (dropSupersededPlay(playToken, track?.id, 'pre_dispatch')) return;

        if (renderer && !localNativeZone && !routeExclusive) {
          this._recordPlayUriDispatch(); formatAttempt = beginFormatAttempt(renderer, track?.id ?? qid, resolvedFormat, srcRate, srcDepth, () => this._playTrack(track, { intent: this._playIntent }), () => replayStaleWhy(playToken, renderer.id, track?.id));
          this._lastPlayUriFormatId = resolvedFormat;
          this._prebufferedForQueueIndex = -1;
          clearGaplessPrefetchIndex();
          const azSent = store.discovery?.activeZone as any;
          diagnosticLog('info', 'transport', `playUri sent to ${renderer.name || renderer.id}`, {
            renderer: renderer.name, trackId: track?.id, urlLength: (decision.effectiveUrl || '').length,
            rendererType: /system (output|audio)/i.test(String(azSent?.name || '')) ? 'browser' : (azSent?.transport_type || azSent?.type),
          });
          await TransportCommand.playUri(decision.effectiveUrl, renderer.id, resolvedFormat, {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: decision.targetFormat.bitDepth || srcDepth,
            sampleRate: decision.targetFormat.sampleRate || srcRate,
            _streamRouterAction: decision.action,
            _streamRouterReason: decision.reason,
          }, { userInitiated: intent === 'user' });
          markNetworkPlayAttemptStarted(renderer.id);
          queueMicrotask(prefetchAlternateFormatAfterPlay);
          queueMicrotask(() => {
            if (!this._isFailureRecoveryActive()) this._prebufferNextTrack();
          });
          queueMicrotask(() => {
            if (!this._isFailureRecoveryActive()) this._prefetchNextStreamUrl();
          });
        } else {
          const streamTrack = {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: decision.targetFormat.bitDepth || srcDepth,
            sampleRate: decision.targetFormat.sampleRate || srcRate,
          };
          const canExclusiveStream = await TransportCommand.shouldUseExclusive(streamTrack);
          if (!canExclusiveStream) diagnosticLog('info', 'transport', 'playUri sent to local audio', {
            renderer: 'local', rendererType: 'browser', trackId: track?.id,
            urlLength: (decision.effectiveUrl || '').length,
          });
          // Route through TransportCommand.playUri even without a network renderer
          // so _matchUsbDacSampleRate runs (exclusive mode + sample rate matching).
          // playUri's doPlay falls through to the local audio element when rendererId is empty.
          if (canExclusiveStream) {
            this._recordPlayUriDispatch();
            this._lastPlayUriFormatId = resolvedFormat;
            try {
              await TransportCommand.playUri(
                decision.effectiveUrl,
                '',
                resolvedFormat,
                streamTrack,
                { suppressExclusiveErrorUi: true },
              );
              commitExclusiveDispatchOutputPath(); // #10426-a1: 'shared' when the Connector reported output_mode shared
            } catch (err: any) {
              const errMsg = String(err?.message || err || '');
              const { getClientTransport } = await import('../handlers/ClientTransportHandler');
              const ct = getClientTransport();
              try {
                await runExclusiveSharedFallback({
                  errMsg,
                  track: streamTrack,
                  zone: store.discovery?.activeZone,
                  branch: 'streaming_exclusive_fallback_shared',
                  playShared: async () => {
                    this._recordPlayUriDispatch();
                    this._lastPlayUriFormatId = resolvedFormat;
                    await ct.playLocalStream({
                      ...streamTrack,
                      streamUrl: decision.effectiveUrl,
                    }, { sharedFallback: true });
                  },
                });
              } catch {
                return;
              }
            }
          } else {
            await TransportCommand.playUri(decision.effectiveUrl, '', resolvedFormat, streamTrack);
          }
          queueMicrotask(prefetchAlternateFormatAfterPlay);
        }
        noteProxyUnreachableRecovery(options?.proxyUnreachableAttempt);
        this._consecutiveSkips = 0;
      } catch (err: any) {
        const errMsg = String(err?.message || err || '');
        if (dropSupersededPlay(playToken, track?.id, 'failure_handling')) { noteSupersededFormatFailure(formatAttempt, errMsg); return; }
        if (isPlayUriNotSentError(errMsg)) { diagnosticLog('info', 'playback', 'play_not_sent_superseded', { trackId: track?.id }); return; } // replaced by a zone switch / user pick — not a failure, not a replay
        console.error('[QueueController] Stream URL resolution failed:', err);

        if (await scheduleStreamingProxyUnreachableRetry(
          errMsg,
          options?.proxyUnreachableAttempt ?? 1,
          () => {
            this._isPlayTrackInFlight = false;
            this._inFlightTrackId = null;
          },
          async (nextAttempt) => { if (isPlayTrackCurrent(playToken)) await this._playTrack(track, { // never re-play a track the user left
            intent: options?.intent ?? this._playIntent,
            proxyUnreachableAttempt: nextAttempt,
          }); },
        )) {
          return;
        }

        // "not found" means the renderer disappeared from discovery — skipping
        // to the next track won't help since the same renderer is unreachable.
        // Mark as lost so auto-recovery can resume when the renderer returns.
        const isRendererGone = errMsg.toLowerCase().includes('not found');
        if (isRendererGone) {
          const zone = store.discovery?.activeZone;
          this._rendererLostAt = Date.now();
          this._rendererLostId = zone?.id || null;
          console.warn('[QueueController] Renderer not found — pausing. Will auto-resume when renderer returns.');
          this._consecutiveSkips = 0;
          this._restorePlaybackSnapshot(priorPlayback);
          store.setPlaybackState('paused');
          return;
        }
        if (await replayAtLowerFormatIfCeiling(errMsg, () => { this._isPlayTrackInFlight = false; this._inFlightTrackId = null; }, formatAttempt)) return; // learned format ceiling: one lower-tier replay
        // "Command timeout" means the Connector's play_uri IPC didn't respond
        // in time — the renderer may still be processing the SOAP request.
        // Rapid-fire skipping floods the renderer with overlapping commands,
        // making things worse. Pause instead of cascading skips.
        const isTimeout = errMsg.toLowerCase().includes('command timeout') || errMsg.toLowerCase().includes('timed out');
        if (isTimeout) {
          const renderer = store.activeConnectorRenderer;
          let verifiedPlaying = false;
          if (renderer?.id) {
            try {
              const C = await import('../lib/connector');
              const { verifyRendererPlayingAfterTimeout } = await import('../lib/ipcTransportDegraded');
              verifiedPlaying = await verifyRendererPlayingAfterTimeout(
                (id) => C.getTransportInfo(id),
                renderer.id,
              );
            } catch { /* verify best-effort */ }
          }
          if (verifiedPlaying || isPlayUriPending(track?.id)) {
            console.warn('[QueueController] Command timeout but renderer PLAYING/PENDING — not pausing or retrying playUri');
            this._consecutiveSkips = 0;
            if (!isPlayUriPending(track?.id)) {
              store.setPlaybackState('playing');
            }
            return;
          }
          console.warn('[QueueController] Command timeout — pausing instead of skip-cascade. Renderer may still be processing.');
          this._consecutiveSkips = 0;
          this._restorePlaybackSnapshot(priorPlayback);
          store.setPlaybackState('paused');
          return;
        }

        const renderer = store.activeConnectorRenderer;
        if (this._playIntent === 'queue-advance') {
          markTransportPlayFailure(renderer?.id || store.discovery?.activeZone?.id);
          showNetworkPlayFailureToast(
            errMsg,
            renderer?.id || store.discovery?.activeZone?.id,
            renderer?.name || store.discovery?.activeZone?.name,
            store.currentTrack,
            store.currentStreamFormatId,
          );
        }

        await handleQueuePlayFailure(this, priorPlayback, { errMsg });
      }
    } finally {
      if (isPlayTrackCurrent(playToken)) {
        this._isPlayTrackInFlight = false;
        this._inFlightTrackId = null;
        _lastPlayTrackCompletedAt = Date.now();
      }
    }
  }

  /**
   * Send a single stream URL to all multizone zones in parallel.
   * Uses Connector.multizonePlay (batch IPC) with fallback to
   * parallel individual playUri calls.
   */
  private async _playTrackMultizone(url: string, formatId: number, track: any) {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;

    this._recordPlayUriDispatch();
    this._lastPlayUriFormatId = formatId;
    store.setPlaybackState('playing');

    try {
      const { isConnectorAvailable, multizonePlay, playUri: connectorPlayUri } = await import('../lib/connector');
      if (!isConnectorAvailable()) {
        console.warn('[QueueController] Multizone requires Connector');
        store.setPlaybackState('paused');
        return;
      }

      try {
        const results = await multizonePlay(
          zones.map(z => ({ renderer_id: z.zoneId, volume: z.actualVolume ?? null })),
          url,
          track?.title || '', track?.artist || '', track?.album || '',
          formatId,
          Math.round(track?.duration || 0),
          track?.artworkUrl || track?.artUrl,
        );
        const anyOk = results.some(r => r.success);
        if (anyOk) {
          store.setRendererHasStream(true);
          console.log('[QueueController] Multizone play results:', results);
        } else {
          console.error('[QueueController] All multizone zones failed:', results);
          store.setPlaybackState('paused');
        }
      } catch (batchErr) {
        console.warn('[QueueController] multizonePlay failed, falling back to parallel playUri:', batchErr);
        const results = await Promise.allSettled(
          zones.map(z =>
            connectorPlayUri(
              z.zoneId, url,
              track?.title || '', track?.artist || '', track?.album || '',
              formatId, Math.round(track?.duration || 0),
              track?.artworkUrl || track?.artUrl,
            )
          )
        );
        const anyOk = results.some(r => r.status === 'fulfilled');
        if (anyOk) store.setRendererHasStream(true);
        else store.setPlaybackState('paused');
      }
    } catch (err) {
      console.error('[QueueController] Multizone play failed:', err);
      store.setPlaybackState('paused');
    }
  }

  /**
   * Play a local library track, routing to the correct backend based on the
   * active zone:
   *   - Multizone group  → Connector.multizonePlay via _playTrackMultizone
   *   - Network renderer → TransportCommand.playUri (UPnP / iOS bridge)
   *   - Local / browser  → ClientTransport.playLocalStream (HTML5 audio)
   *
   * This mirrors the routing used for Qobuz tracks so local files are
   * first-class citizens of every playback path. Before this was added,
   * local files on a network DAC would silently fail because playLocalStream
   * bails out when a network zone is active.
   */
  private async _hydrateLocalTrackFromLibrary(track: any): Promise<any> {
    if (track?.file_path) return track;
    const trackKey = String(track?.id || track?.trackId || '');
    const shouldHydrate =
      trackKey.startsWith('local:')
      || track?.source === 'local_library'
      || isLocalLibraryTrack(track);
    if (!shouldHydrate || !trackKey) return track;
    try {
      const { libraryService } = await import('../services/libraryService');
      const detail: any = await libraryService.getTrack(trackKey);
      const row = detail?.track || detail;
      const filePath = row?.file_path || row?.filePath;
      if (!filePath) return track;
      return {
        ...track,
        source: 'local_library',
        file_path: filePath,
        machine_id: row?.machine_id || track?.machine_id,
        format: row?.format || track?.format,
        bit_depth: row?.bit_depth ?? track?.bit_depth,
        sample_rate: row?.sample_rate ?? track?.sample_rate,
        duration: resolveQueueTrackDuration(track, row) ?? durationFromTrackRow(track) ?? durationFromTrackRow(row),
        embedded_cover_hash: row?.embedded_cover_hash ?? track?.embedded_cover_hash,
        folder_cover_hash: row?.folder_cover_hash ?? track?.folder_cover_hash,
        album_key: row?.album_key ?? track?.album_key,
        albumId: track?.albumId || track?.album_key || row?.album_key || null,
        album: track?.album || row?.album || row?.album_title || null,
        artworkUrl: track?.artworkUrl || row?.cover_art_url || row?.artwork_url || null,
      };
    } catch (e) {
      console.warn('[QueueController] hydrate local track failed:', trackKey, e);
      return track;
    }
  }

  private async _playLocalLibraryTrack(
    track: any,
    ctx: {
      isMultizone: boolean;
      renderer: any;
      localOnlyLocked?: boolean;
      proxyUnreachableAttempt?: number;
    },
    retried = false,
  ): Promise<void> {
    const store = useMaestroStore.getState();
    clearPlaybackNetworkFormatLabel();
    const { isMultizone } = ctx;
    // Renderer recovery: re-derive from activeZone only when this play was not
    // locked to local at entry (saved-zone restore must not redirect mid-flight).
    let renderer = ctx.renderer;
    if (!renderer && !ctx.localOnlyLocked && !isMultizone && _isRemoteRenderer()) {
      const az = store.discovery?.activeZone as any;
      if (az) {
        renderer = connectorRendererFromZone(az);
        store.setActiveConnectorRenderer(renderer);
        console.log('[QC] _playLocalLibraryTrack: recovered renderer from activeZone:', renderer.name);
      }
    }

    if (!isMultizone && !renderer && isDsd(track) && !isLocalNativeZone(store.discovery?.activeZone)) {
      const canExclusive = await TransportCommand.shouldUseExclusive(track);
      if (!canExclusive) {
        const nativeZones = (store.discovery?.discoveredZones || []).filter(
          (z: { type?: string }) => z.type === 'local-native',
        );
        const dacZone = nativeZones.length === 1
          ? nativeZones[0]
          : nativeZones.find((z: { metadata?: { audio_device_id?: string }; name?: string }) => {
              const selectedId = store.selectedUsbDac?.id;
              if (!selectedId) return false;
              const audioId = z.metadata?.audio_device_id;
              return audioId === selectedId || z.name === selectedId;
            }) || nativeZones[0];
        console.warn('[QueueController] DSD playback blocked — no exclusive USB DAC path', {
          file_path: track.file_path,
          zone_id: store.discovery?.activeZone?.id,
          local_native_available: nativeZones.length,
        });
        store.setPlaybackState('paused');
        showImperativeToast(
          dacZone
            ? `DSD needs your USB DAC — switch output to ${dacZone.name}`
            : 'DSD files require a USB DAC with exclusive mode. System Audio cannot decode DSD.',
          6000,
        );
        return;
      }
    }

    // Desktop + network renderer: use play_local_file IPC directly.
    // Rust builds a LAN-reachable signed proxy URL for the renderer's subnet,
    // then sends SOAP SetAVTransportURI + Play in one call (never WebAudioRenderer).
    const { isConnectorAvailable, playLocalFile, networkRendererTransportLabel } = await import('../lib/connector');
    const { isHomeLibraryHost } = await import('../services/localLibraryIpcGate');
    const isDesktopNetworkRenderer = !isMultizone
      && isConnectorAvailable()
      && isHomeLibraryHost()
      && !!renderer?.id
      && _isRemoteRenderer();

    if (isDesktopNetworkRenderer) {
      console.log('[QC] local track → play_local_file IPC (desktop+network renderer)', {
        renderer: renderer.name || renderer.id,
        file: String(track.file_path || '').split('/').pop(),
      });

      const az = store.discovery?.activeZone as any;
      diagnosticLog('info', 'playback', 'local track branch decision', {
        branch: 'play_local_file_ipc',
        renderer_id: renderer.id,
        renderer_type: az?.transport_type || az?.type,
        isDesktopNetworkRenderer: true,
      });

      const transportLabel = networkRendererTransportLabel(az?.transport_type || az?.type);

      diagnosticLog('info', 'playback', 'play_local_file IPC invoke', {
        track_id: track.id,
        file_path_present: !!track.file_path,
        renderer_id: renderer.id,
        transport: transportLabel,
      });

      try {
        const playResult = await playLocalFile(
          track.file_path,
          renderer.id,
          track.title || '',
          track.artist || '',
          track.album || '',
          Math.round(track.duration || 0),
          track.embedded_cover_hash || track.folder_cover_hash || null,
          track.sample_rate ?? null,
          track.bit_depth ?? null,
          track.channels ?? null,
          track.file_size ?? null,
        );

        diagnosticLog('info', 'playback', 'network renderer play dispatch', {
          success: playResult.success,
          transport: playResult.transport || transportLabel,
          stream_host: playResult.streamHost ?? null,
          stream_url_truncated: String(playResult.proxyUrl || '').slice(0, 160),
          set_av_transport_uri: playResult.setAvTransportUri?.status ?? null,
          set_av_detail: playResult.setAvTransportUri?.detail ?? null,
          error: playResult.error ?? null,
        });

        if (!playResult.success) {
          const errMsg = String(playResult.error || 'Network renderer play failed');
          console.error('[QC] play_local_file dispatch failed:', errMsg);
          diagnosticLog('info', 'playback', 'play_local_file IPC result', {
            ok: false,
            result_url_or_error_truncated: errMsg.slice(0, 120),
          });

          if (await scheduleLocalProxyUnreachableRetry(
            errMsg,
            ctx.proxyUnreachableAttempt ?? 1,
            () => this._playLocalLibraryTrack(
              track,
              { ...ctx, proxyUnreachableAttempt: (ctx.proxyUnreachableAttempt ?? 1) + 1 },
              retried,
            ),
          )) {
            return;
          }

          if (!retried && track.file_path) {
            console.log('[QC] retrying local track via play_local_file');
            return this._playLocalLibraryTrack(track, ctx, true);
          }

          handleLocalPlayFailure(errMsg, track.title || 'This track', track.file_path);
          useMaestroStore.getState().setPlaybackState('paused');
          return;
        }

        recordLocalPlaySuccess(track.file_path);

        const proxyUrl = playResult.proxyUrl;

        diagnosticLog('info', 'playback', 'play_local_file IPC result', {
          ok: true,
          result_url_or_error_truncated: String(proxyUrl || '').slice(0, 120),
        });
        const s = useMaestroStore.getState();
        if (s.currentTrack?.id !== track.id && s.currentTrack?.file_path !== track.file_path) {
          console.log('[QC] local track changed during play_local_file — aborting');
          return;
        }

        s.setCurrentStreamUrl(proxyUrl);
        s.setCurrentStreamFormatId(pickFormatIdForLocalFile(track, renderer, false));
        const { setPlaybackNetworkFormatLabel } = await import('../utils/playbackNetworkFormatLabel');
        setPlaybackNetworkFormatLabel(playResult.networkFormatLabel ?? null);
        s.setRendererHasStream(true);
        s.setPlaybackState('buffering');
        const opId = useOperationProgressStore.getState().beginOperation('Buffering audio...', {
          progress: 0.15,
        });
        window.setTimeout(() => {
          const now = useMaestroStore.getState();
          const stillCurrent =
            now.currentTrack?.id === track.id || now.currentTrack?.file_path === track.file_path;
          if (stillCurrent && now.playbackState === 'buffering') {
            now.setPlaybackState('playing');
          }
          useOperationProgressStore.getState().endOperation(opId);
        }, 1500);
        this._recordPlayUriDispatch();
        this._lastPlayUriFormatId = pickFormatIdForLocalFile(track, renderer, false);
        this._consecutiveSkips = 0;
        markNetworkPlayAttemptStarted(renderer.id);
        this._recordRecentLocalAlbumOnPlaybackStart(track);
        noteProxyUnreachableRecovery(ctx.proxyUnreachableAttempt);

        const { getClientTransport } = await import('../handlers/ClientTransportHandler');
        const ws = getClientTransport().getWebSocket();
        queueMicrotask(() => prefetchNextLocalTracks(ws, track.machine_id));
        queueMicrotask(() => {
          if (!this._isFailureRecoveryActive()) void this._prebufferNextTrack();
        });
        return;
      } catch (err: any) {
        console.error('[QC] play_local_file IPC failed:', err?.message || err);
        diagnosticLog('info', 'playback', 'network renderer play dispatch', {
          success: false,
          transport: transportLabel,
          stream_host: null,
          stream_url_truncated: null,
          set_av_transport_uri: null,
          set_av_detail: null,
          error: String(err?.message || err || '').slice(0, 200),
        });
        diagnosticLog('info', 'playback', 'play_local_file IPC result', {
          ok: false,
          result_url_or_error_truncated: String(err?.message || err || '').slice(0, 120),
        });

        const errMsg = String(err?.message || err || '');
        if (await scheduleLocalProxyUnreachableRetry(
          errMsg,
          ctx.proxyUnreachableAttempt ?? 1,
          () => this._playLocalLibraryTrack(
            track,
            { ...ctx, proxyUnreachableAttempt: (ctx.proxyUnreachableAttempt ?? 1) + 1 },
            retried,
          ),
        )) {
          return;
        }

        if (!retried && track.file_path) {
          console.log('[QC] retrying local track via play_local_file');
          return this._playLocalLibraryTrack(track, ctx, true);
        }

        handleLocalPlayFailure(errMsg, track.title || 'This track', track.file_path);

        const s2 = useMaestroStore.getState();
        if (s2.currentTrack?.id === track.id || s2.currentTrack?.file_path === track.file_path) {
          s2.setPlaybackState('paused');
        }
        return;
      }
    }

    // Fallback: System Audio, iOS/BATON, multizone, or no renderer —
    // use the URL-first resolveLocalFileUrl path.
    const { getClientTransport } = await import('../handlers/ClientTransportHandler');
    const ct = getClientTransport();
    const ws = ct.getWebSocket();

    let url: string;
    try {
      url = await resolveLocalFileUrl(track.file_path, ws, track.machine_id);
    } catch (err: any) {
      console.error('[QueueController] local file resolve failed:', err?.message || err);
      const errMsg = String(err?.message || err || '');
      handleLocalPlayFailure(errMsg, track.title || 'This track', track.file_path);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === track.id || s.currentTrack?.file_path === track.file_path) {
        s.setPlaybackState('paused');
      }
      return;
    }

    recordLocalPlaySuccess(track.file_path);

    // If the user switched tracks while we were resolving, abandon this dispatch.
    const s = useMaestroStore.getState();
    const stillCurrent =
      s.currentTrack?.id === track.id || s.currentTrack?.file_path === track.file_path;
    if (!stillCurrent) {
      console.log('[QueueController] local track changed during URL resolve — aborting');
      return;
    }

    const formatId = pickFormatIdForLocalFile(track, renderer, isMultizone);
    const trackForTransport = {
      ...track,
      streamUrl: url,
      formatId,
      format: track.format,
      bitDepth: track.bit_depth,
      sampleRate: track.sample_rate,
      artUrl: track.artUrl || track.artworkUrl || track.cover_art_url,
    };

    store.setCurrentStreamUrl(url);
    store.setCurrentStreamFormatId(formatId);

    try {
      if (isMultizone) {
        const azMz = store.discovery?.activeZone as any;
        diagnosticLog('info', 'playback', 'local track branch decision', {
          branch: 'multizone',
          renderer_id: renderer?.id || null,
          renderer_type: azMz?.transport_type || azMz?.type,
          isDesktopNetworkRenderer: false,
        });
        await this._playTrackMultizone(url, formatId, trackForTransport);
      } else if (!renderer?.id || !_isRemoteRenderer()) {
        const azLocal = store.discovery?.activeZone as any;
        const trackIsDsd = isDsd(track);
        const canExclusive = await TransportCommand.shouldUseExclusive(track);
        const localStreamUrl = isLocalStreamUrl(url);
        // DSD: unchanged — exclusive when capable (any URL). PCM: local proxy URL only.
        const useExclusive = canExclusive && (trackIsDsd || localStreamUrl);

        if (useExclusive) {
          const branch = trackIsDsd ? 'exclusive_dsd' : 'exclusive_pcm';
          diagnosticLog('info', 'playback', 'local track branch decision', {
            branch,
            renderer_id: renderer?.id || 'local-native',
            renderer_type: azLocal?.transport_type || azLocal?.type || 'local-native',
            isDesktopNetworkRenderer: false,
          });
          this._recordPlayUriDispatch();
          this._lastPlayUriFormatId = formatId;
          if (trackIsDsd) {
            console.log('[QC] local DSD track → exclusive engine via playUri', {
              zone: store.discovery?.activeZone?.name || 'USB DAC',
              file: String(track.file_path || '').split('/').pop(),
              dsd_hz: track.sample_rate ?? track.dsd_rate,
            });
          } else {
            console.log('[QC] local PCM track → exclusive engine via playUri', {
              zone: store.discovery?.activeZone?.name || 'USB DAC',
              file: String(track.file_path || '').split('/').pop(),
              sample_rate: track.sample_rate,
              bit_depth: track.bit_depth,
            });
          }
          try {
            await TransportCommand.playUri(
              url,
              '',
              formatId,
              trackForTransport,
              trackIsDsd ? undefined : { suppressExclusiveErrorUi: true },
            );
            commitExclusiveDispatchOutputPath(); // #10426-a1: 'shared' when the Connector reported output_mode shared
          } catch (err: any) {
            if (trackIsDsd) {
              throw err;
            }
            const errMsg = String(err?.message || err || '');
            try {
              await runExclusiveSharedFallback({
                errMsg,
                track: trackForTransport,
                zone: azLocal,
                branch: 'exclusive_pcm_fallback_shared',
                playShared: async () => {
                  this._recordPlayUriDispatch();
                  this._lastPlayUriFormatId = formatId;
                  await ct.playLocalStream({ ...trackForTransport, streamUrl: url }, { sharedFallback: true });
                },
              });
            } catch {
              return;
            }
          }
        } else if (trackIsDsd && !isLocalNativeZone(azLocal)) {
          diagnosticLog('info', 'playback', 'local track branch decision', {
            branch: 'dsd_blocked_no_exclusive',
            renderer_id: renderer?.id || 'local',
            renderer_type: azLocal?.transport_type || azLocal?.type || 'local',
            isDesktopNetworkRenderer: false,
          });
          store.setPlaybackState('paused');
          showImperativeToast(
            'DSD files require a USB DAC with exclusive mode. This output cannot decode DSD.',
            6000,
          );
          return;
        } else {
          diagnosticLog('info', 'playback', 'local track branch decision', {
            branch: 'playLocalStream',
            renderer_id: renderer?.id || 'local',
            renderer_type: azLocal?.transport_type || azLocal?.type || 'local',
            isDesktopNetworkRenderer: false,
          });
          // System Audio / browser PCM — HTML5 audio via WebAudioRenderer (required for
          // remote tunnel URLs; playUri + Tauri exclusive path cannot stream trycloudflare).
          store.setUsbOutputPath(null);
          store.setUsbOutputFallbackNotice(null);
          this._recordPlayUriDispatch();
          this._lastPlayUriFormatId = formatId;
          console.log('[QC] local track → playLocalStream', {
            zone: store.discovery?.activeZone?.name || 'System Audio',
            file: String(track.file_path || '').split('/').pop(),
          });
          const sharedDevice = resolveExclusiveDeviceName(azLocal, store.selectedUsbDac) || 'System Audio';
          logLocalOutputShared(sharedDevice, 'no_exclusive_capable');
          await ct.playLocalStream({ ...trackForTransport, streamUrl: url });
        }
      } else {
        const azUri = store.discovery?.activeZone as any;
        diagnosticLog('info', 'playback', 'local track branch decision', {
          branch: 'playUri',
          renderer_id: renderer?.id,
          renderer_type: azUri?.transport_type || azUri?.type,
          isDesktopNetworkRenderer: false,
        });
        this._recordPlayUriDispatch();
        this._lastPlayUriFormatId = formatId;
        console.log('[QC] local track → playUri', {
          renderer: renderer?.name || '(network)',
          formatId,
          file: String(track.file_path || '').split('/').pop(),
        });
        await TransportCommand.playUri(url, renderer.id, formatId, trackForTransport);
        markNetworkPlayAttemptStarted(renderer.id);
      }
    } catch (err: any) {
      console.error('[QueueController] local track dispatch failed:', err?.message || err);

      const errMsg = String(err?.message || err || '');
      if (await scheduleLocalProxyUnreachableRetry(
        errMsg,
        ctx.proxyUnreachableAttempt ?? 1,
        () => this._playLocalLibraryTrack(
          track,
          { ...ctx, proxyUnreachableAttempt: (ctx.proxyUnreachableAttempt ?? 1) + 1 },
          retried,
        ),
      )) {
        return;
      }

      // On first failure, evict the cached URL and retry with a fresh registration.
      if (!retried && track.file_path) {
        const { invalidateCachedUrl } = await import('../lib/playback-bridge');
        invalidateCachedUrl(track.file_path);
        invalidateCachedUrl(url);
        console.log('[QueueController] retrying local track with fresh URL');
        return this._playLocalLibraryTrack(track, ctx, true);
      }

      markTransportPlayFailure(renderer?.id);
      if (this._playIntent === 'queue-advance') {
        showNetworkPlayFailureToast(
          errMsg,
          renderer?.id,
          renderer?.name || store.discovery?.activeZone?.name,
          track,
          formatId,
        );
      } else {
        showUserPlayRetryToast(
          track.title || 'This track',
          renderer?.name || store.discovery?.activeZone?.name || 'your device',
          errMsg,
          track,
          formatId,
          renderer?.id,
        );
      }

      const s2 = useMaestroStore.getState();
      if (s2.currentTrack?.id === track.id || s2.currentTrack?.file_path === track.file_path) {
        s2.setPlaybackState('paused');
        void TransportCommand.pause(true).catch(() => {});
      }
      return;
    }

    noteProxyUnreachableRecovery(ctx.proxyUnreachableAttempt);
    this._recordRecentLocalAlbumOnPlaybackStart(track);
    queueMicrotask(() => prefetchNextLocalTracks(ws, track.machine_id));
  }

  /**
   * Pre-buffer the next track on the active UPnP renderer via SetNextAVTransportURI.
   * This gives gapless playback: when the current track ends, the renderer seamlessly
   * transitions to the correct next track instead of falling back to its internal
   * queue (which causes a random-track flash on devices like NAD/BluOS).
   *
   * Non-critical — if this fails, the useRemoteAdvanceTimer fallback still works.
   */
  private async _prebufferNextTrack() { logSetNextPrefetchOutcome(await this._prebufferNextTrackInner(), useMaestroStore.getState().queueIndex); }
  /** Returns why the next track was not armed; `undefined` = armed (stop evidence, Paul Oct 23). */
  private async _prebufferNextTrackInner(): Promise<string | undefined> {
    if (deferUntilPlayUriConfirmed(useMaestroStore.getState().currentTrack?.id, () => { void this._prebufferNextTrack(); })) return 'deferred_until_play_confirmed';
    const prefetchGeneration = getAdvanceGeneration();
    try {
      if (this._isFailureRecoveryActive()) return 'failure_recovery_active';
      if (!(window as any).__TAURI__) return 'no_connector';

      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      if (!renderer) return 'no_renderer';
      const zone =
        store.discovery?.activeZone?.id === renderer.id
          ? store.discovery.activeZone
          : store.discovery?.discoveredZones?.find((z) => z.id === renderer.id);

      const useSetNextPrefetch = shouldAttemptSetNextPrefetch(renderer, zone);
      if (!useSetNextPrefetch && shouldSkipGaplessPrefetch(renderer, zone)) return 'gapless_prefetch_skipped';
      if (store.repeatMode === 'one') return 'repeat_one';

      const { queue, queueIndex, shuffled, shuffleOrder } = store;
      let nextQueuePos = queueIndex + 1;

      if (nextQueuePos >= queue.length) {
        if (store.repeatMode === 'all') {
          nextQueuePos = 0;
        } else {
          return 'end_of_queue';
        }
      }

      const actualIdx = shuffled && shuffleOrder.length > nextQueuePos
        ? shuffleOrder[nextQueuePos] : nextQueuePos;
      const nextItem = queue[actualIdx];
      if (!nextItem?.track) return 'no_next_track';

      const nextTrack = nextItem.track;

      const mz = store.multizone;
      const isMultizone = mz.enabled && mz.groupedZones.length >= 2;
      if (isMultizone) return 'multizone';

      let nextUri: string;
      let nextFormatId: number;
      let localFileMeta: {
        sampleRate?: number | null;
        bitDepth?: number | null;
        channelCount?: number | null;
        fileSize?: number | null;
      } | undefined;

      if (isLocalLibraryTrack(nextTrack)) {
        if (!connectorSupportsLocalLibrarySetNext()) return 'local_setnext_unsupported';
        if (!nextTrack.file_path) return 'no_file_path';
        nextUri = nextTrack.file_path;
        nextFormatId = pickFormatIdForLocalFile(nextTrack, renderer, false);
        localFileMeta = {
          sampleRate: nextTrack.sample_rate ?? null,
          bitDepth: nextTrack.bit_depth ?? null,
          channelCount: nextTrack.channels ?? null,
          fileSize: nextTrack.file_size ?? null,
        };
      } else {
        const qid = nextTrack.qobuzTrackId || nextTrack.serviceTrackId || nextTrack.id;
        if (!qid || isLocalTrackId(qid) || isLocalLibraryTrack(nextTrack)) return 'no_stream_id';

        const formatId = getFormatIdForRenderer(renderer);
        const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
        if (!data?.url) return `stream_url_unavailable format=${formatId}`;
        nextUri = data.url;
        nextFormatId = data.formatId || formatId;
      }

      const curStore = useMaestroStore.getState();
      if (curStore.queueIndex !== queueIndex) return 'queue_moved';
      if (curStore.activeConnectorRenderer?.id !== renderer.id) return 'renderer_changed';

      const { setNextUri } = await import('../lib/connector');
      await setNextUri(
        renderer.id,
        nextUri,
        nextTrack.title || '',
        nextTrack.artist || '',
        nextTrack.album || '',
        nextFormatId,
        Math.round(nextTrack.duration || 0),
        nextTrack.artworkUrl || nextTrack.artUrl || nextTrack.embedded_cover_hash || nextTrack.folder_cover_hash,
        localFileMeta,
      );

      if (getAdvanceGeneration() !== prefetchGeneration) {
        console.log('[QC] Pre-buffer abandoned — queue navigation superseded prefetch');
        return 'superseded';
      }

      this._prebufferedForQueueIndex = nextQueuePos;
      setGaplessPrefetchIndex(nextQueuePos);
      if (useSetNextPrefetch) {
        markSetNextFillConfirmed(nextQueuePos);
      }
      console.log('[QC] Pre-buffered next track for gapless:', nextTrack.title);
    } catch (e) {
      console.debug('[QC] Pre-buffer next track failed (non-critical):', e);
      this._prebufferedForQueueIndex = -1;
      clearGaplessPrefetchIndex();
      clearSetNextFillConfirmed();
      return `failed: ${e instanceof Error ? e.message : String(e)}`;
    }
  }

  /**
   * Pre-resolve the next track's Qobuz stream URL for no-SetNext renderers.
   * Does NOT call SetNextAVTransportURI — only warms the URL cache for fast advance.
   */
  private async _prefetchNextStreamUrl() {
    try {
      if (this._isFailureRecoveryActive()) return;
      if (!(window as any).__TAURI__) return;

      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      if (!renderer) return;

      const zone =
        store.discovery?.activeZone?.id === renderer.id
          ? store.discovery.activeZone
          : store.discovery?.discoveredZones?.find((z) => z.id === renderer.id);

      if (shouldAttemptSetNextPrefetch(renderer, zone)) return;
      if (!isTimerBasedRenderer(renderer, zone)) return;
      if (store.repeatMode === 'one') return;

      const { queue, queueIndex, shuffled, shuffleOrder } = store;
      let nextQueuePos = queueIndex + 1;
      if (nextQueuePos >= queue.length) {
        if (store.repeatMode === 'all') {
          nextQueuePos = 0;
        } else {
          return;
        }
      }

      const actualIdx = shuffled && shuffleOrder.length > nextQueuePos
        ? shuffleOrder[nextQueuePos]
        : nextQueuePos;
      const nextItem = queue[actualIdx];
      if (!nextItem?.track) return;

      const nextTrack = nextItem.track;
      if (isLocalLibraryTrack(nextTrack)) return;

      const qid = nextTrack.qobuzTrackId || nextTrack.serviceTrackId || nextTrack.id;
      if (!qid || isLocalTrackId(qid) || isLocalLibraryTrack(nextTrack)) return;

      const formatId = getFormatIdForRenderer(renderer);
      const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
      if (!data?.url) return;

      const curStore = useMaestroStore.getState();
      if (curStore.queueIndex !== queueIndex) return;
      if (curStore.activeConnectorRenderer?.id !== renderer.id) return;

      this._prefetchedStream = {
        queueIndex: nextQueuePos,
        trackId: String(qid),
        url: data.url,
        formatId: data.formatId || formatId,
        format: data.format,
        samplingRate: data.samplingRate,
        bitDepth: data.bitDepth,
      };
      console.log('[QC] Pre-resolved next stream URL for timer-based renderer:', nextTrack.title);
    } catch (e) {
      console.debug('[QC] Pre-resolve next stream URL failed (non-critical):', e);
      this._clearPrefetchedStream();
    }
  }

  /**
   * Renderer gaplessly advanced to the prefetched next track — sync queue/UI
   * without issuing another playUri (which would restart the track).
   */
  adoptRendererGaplessTransition(nextQueueIndex: number, positionSec: number): void {
    if (this._isFailureRecoveryActive()) {
      console.log('[QueueController] gapless transition suppressed — failure recovery active');
      return;
    }
    const store = useMaestroStore.getState();
    if (store.queueIndex === nextQueueIndex) {
      const dur = store.duration || store.currentTrack?.duration || 0;
      const rawDur = dur > 10000 ? dur / 1000 : dur;
      store.updateTime(positionSec, rawDur || dur);
      void import('./gaplessHandoffRearm').then((m) => m.ensureGaplessPrefetchArmedAfterHandoff());
      return;
    }

    this.suppressAdvance(120_000, 'gapless_adopt');
    cancelRemoteAdvanceTimer();
    markRendererGaplessAdvance();
    this._prebufferedForQueueIndex = -1;
    clearGaplessPrefetchIndex();
    clearSetNextFillConfirmed();
    this._clearSetNextHandoffWatchdog();

    const { queue, shuffled, shuffleOrder } = store;
    const actualIdx = shuffled && shuffleOrder.length > nextQueueIndex
      ? shuffleOrder[nextQueueIndex]
      : nextQueueIndex;
    const nextItem = queue[actualIdx];
    if (!nextItem?.track) return;

    const track = nextItem.track;
    const trackDuration = durationFromTrackRow(track);

    store.setQueueIndex(nextQueueIndex);
    store.setCurrentTrack(track);
    store.updateTime(positionSec, trackDuration ?? store.duration);
    store.setPlaybackState('playing');
    this._recordPlayUriDispatch();
          this._consecutiveSkips = 0;

    diagnosticLog(
      'info',
      'playback',
      `Renderer gapless transition: ${track.title || '?'} by ${track.artist || '?'}`,
      { queuePosition: nextQueueIndex, positionSec },
    );

    queueMicrotask(() => this._prebufferNextTrack());

    const zone = this._resolveActiveZone();
    if (
      positionSec <= 1
      && isTimerBasedRenderer(store.activeConnectorRenderer, zone)
      && isGaplessSetNextEnabled()
      && rendererSupportsSetNext(store.activeConnectorRenderer, zone)
    ) {
      this._scheduleGaplessHandoffPlaybackVerify(getAdvanceGeneration(), nextQueueIndex);
    }
  }

  /**
   * Resolve a Qobuz stream URL for a track at the correct format for the
   * given renderer. Returns { url, formatId } or null.
   */
  async resolveStreamUrl(
    track: any,
    overrideRenderer?: { id: string; name?: string; manufacturer?: string } | null,
  ): Promise<{ url: string; formatId: number } | null> {
    if (isLocalLibraryTrack(track)) return null;

    const qid = track?.qobuzTrackId || track?.serviceTrackId || track?.id;
    if (!qid || isLocalTrackId(qid)) return null;

    const renderer = overrideRenderer !== undefined
      ? overrideRenderer
      : useMaestroStore.getState().activeConnectorRenderer;
    const formatId = getFormatIdForRenderer(renderer);

    try {
      const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
      const url = data?.url || '';
      if (!url) return null;
      return { url, formatId: data?.formatId || formatId };
    } catch {
      return null;
    }
  }

  // === Public API ===

  async next() {
    cancelFormatCeilingReplay('user_next');
    getSyncHandler().claimControl();
    TransportState.claimTransportOwner('queue');
    bumpAdvanceGeneration();
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    this.suppressAdvance(5000, 'user_skip');
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();
    const fromItem = store.queue[store.queueIndex]?.track;

    const nextIndex = store.queueIndex + 1;
    if (nextIndex < store.queue.length) {
      store.setQueueIndex(nextIndex);
      const item = store.queue[nextIndex];
      const toTr = item?.track;
      diagnosticLog(
        'info',
        'playback',
        `Auto-advance from "${fromItem?.title || ''}" to "${toTr?.title || ''}"`,
        { fromTrackId: fromItem?.id, toTrackId: toTr?.id, reason: 'skip' },
      );
      if (item?.track) await this._playTrack(item.track, { intent: 'user' });
    } else if (store.repeatMode === 'all') {
      store.setQueueIndex(0);
      const item = store.queue[0];
      const toTr = item?.track;
      diagnosticLog(
        'info',
        'playback',
        `Auto-advance from "${fromItem?.title || ''}" to "${toTr?.title || ''}"`,
        { fromTrackId: fromItem?.id, toTrackId: toTr?.id, reason: 'skip' },
      );
      if (item?.track) await this._playTrack(item.track, { intent: 'user' });
    } else {
      const handled = await this._handleEllaRadioAtQueueEnd('user');
      if (handled) return;
      console.log('[QueueController] next() at end of playlist — stopping playback');
      store.setPlaybackState('stopped');
      clearQueueOwnershipAcceptanceLatch('playlist_end');
      TransportState.releaseTransportOwner({ force: true });
    }
  }

  /** Stop active queue playback when a non-queue source takes over. Queue contents stay visible. */
  stopForOwnershipTransfer(): void {
    this.suppressAdvance(120_000, 'ownership_transfer');
    if (!TransportState.isQueueOwner() && !shouldRetainQueueTransportOwnership()) return;
    clearQueueOwnershipAcceptanceLatch('ownership_transfer');
    const store = useMaestroStore.getState();
    const active = store.playbackState === 'playing' || store.playbackState === 'loading';
    if (!active) {
      TransportState.releaseTransportOwner({ force: true });
      return;
    }
    void TransportCommand.pause(true).catch(() => {});
    const renderer = store.activeConnectorRenderer;
    if (renderer?.id) void TransportCommand.stopRenderer(renderer.id, { caller: 'ownership_transfer', userInitiated: true }).catch(() => {});
    else {
      try { TransportCommand.pauseLocalAudioElement(); } catch { /* best effort */ }
    }
    store.setPlaybackState('stopped');
    store.setRendererHasStream(false);
    TransportState.releaseTransportOwner({ force: true });
  }

  async clearQueue(): Promise<void> {
    getSyncHandler().claimControl();
    const store = useMaestroStore.getState();
    const wasActive = store.playbackState === 'playing' || store.playbackState === 'loading';
    const renderer = store.activeConnectorRenderer;

    // Bound + generation bump: suppress teardown STOPPED phantoms for the replace
    // window only. Cleared on playAll/playFrom/playSingle or confirmed play / timeout.
    if (TransportState.isQueueOwner() || wasActive) {
      bumpAdvanceGeneration();
      this.suppressAdvance(QueueControllerImpl.CLEAR_QUEUE_SUPPRESS_MS, 'clear_queue');
    }

    // Update store immediately so the UI reflects the clear without waiting on transport.
    const preClearQueueLength = store.queue.length;
    const clearQueueTransitionStart = performance.now();
    store.setQueue([], 0);
    store.setCurrentTrack(null);
    logClearQueueTransitionIfSlow(clearQueueTransitionStart, preClearQueueLength);
    store.setPlaybackState('idle');
    store.updateTime(0, 0);
    store.setRendererHasStream(false);
    store.setCurrentStreamUrl(null);
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    this._clearQueueTerminalLatch();
    clearQueueOwnershipAcceptanceLatch('clear_queue');
    TransportState.releaseTransportOwner({ force: true });

    try { localStorage.removeItem('maestro_last_played'); } catch { /* best effort */ }

    getSyncHandler().pushClearedQueue();

    if (wasActive) {
      void TransportCommand.pause(true).catch(() => {});
      if (renderer?.id) {
        void TransportCommand.stopRenderer(renderer.id, { caller: 'clear_queue', userInitiated: true }).catch(() => {});
      } else {
        try { TransportCommand.pauseLocalAudioElement(); } catch { /* best effort */ }
      }
    }
  }

  /** @internal Exclusive poller and TransportCommand call this — must exist on QueueController */
  async advanceTrack(direction: 'next' | 'prev') {
    if (direction === 'prev') return this.previous();
    return this.next();
  }

  async previous() {
    cancelFormatCeilingReplay('user_navigation');
    getSyncHandler().claimControl();
    bumpAdvanceGeneration();
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    this.suppressAdvance(5000, 'user_skip');
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();

    if (store.position > 3) {
      TransportCommand.seek(0);
      return;
    }

    if (store.queueIndex > 0) {
      const prevIndex = store.queueIndex - 1;
      store.setQueueIndex(prevIndex);
      const item = store.queue[prevIndex];
      if (item?.track) await this._playTrack(item.track, { intent: 'user' });
    }
  }

  /**
   * #582 — atomically replace the queue for user-initiated play.
   * Clears persisted/rehydrated survivors, bumps advance + replace generations,
   * and beacons if stale tracks survive the replace.
   */
  private _beginAtomicQueueReplace(
    queueItems: { track: ReturnType<typeof prepareTrackForQueue> }[],
    startIndex: number,
  ): void {
    const store = useMaestroStore.getState();
    const priorN = store.queue.length;
    const priorIds = trackIdsFromQueue(store.queue);
    const intendedIds = trackIdsFromQueue(queueItems);
    const newN = queueItems.length;

    bumpAdvanceGeneration();
    bumpQueueReplaceGeneration();
    try { localStorage.removeItem('maestro_last_played'); } catch { /* best effort */ }

    store.setQueue(queueItems, startIndex);
    store.setActiveSource('library');

    const resultQueue = useMaestroStore.getState().queue;
    const resultN = resultQueue.length;
    const survivorCount = countQueueReplaceSurvivors(priorIds, intendedIds, resultQueue);
    if (resultN !== newN || survivorCount > 0) {
      diagnosticLog('warn', 'playback', 'queue_replace_merged', {
        prior_n: priorN,
        new_n: newN,
        result_n: resultN,
        survivor_count: survivorCount,
      });
    }
  }

  async playAll(tracks: any[], startIndex = 0) {
    cancelFormatCeilingReplay('user_navigation');
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    _lastPlayTrackCompletedAt = 0;
    this._clearRecentLocalAlbumSession();
    this._clearQueueTerminalLatch();
    const queueItems = tracks.map((t: any) => ({ track: prepareTrackForQueue(t) }));
    this._beginAtomicQueueReplace(queueItems, startIndex);
    // Keep clear_queue suppress through first playUri so teardown STOPPED cannot
    // skip the new queue's track 0. Always clear in finally — never leave it stuck.
    try {
      await ensurePlaybackZoneBeforeStart();
      const item = queueItems[startIndex];
      if (item?.track) await this._playTrack(item.track, { intent: 'user' });
    } catch (err) {
      this._clearQueueReplaceSuppression('queue-replace-failed');
      throw err;
    } finally {
      this._clearQueueReplaceSuppression('queue-replace-complete');
    }
  }

  async playFrom(tracks: any[], index: number) {
    cancelFormatCeilingReplay('user_navigation');
    diagnosticLog('info', 'playback', 'playFrom entry', {
      tracks_count: tracks?.length ?? 0,
      start_index: index,
    });
    await this.playAll(tracks, index);
  }

  async playSingle(track: any) {
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    _lastPlayTrackCompletedAt = 0;
    this._clearRecentLocalAlbumSession();
    this._clearQueueTerminalLatch();
    const queueItems = [{ track: prepareTrackForQueue(track) }];
    this._beginAtomicQueueReplace(queueItems, 0);
    try {
      await ensurePlaybackZoneBeforeStart();
      await this._playTrack(track, { intent: 'user' });
    } catch (err) {
      this._clearQueueReplaceSuppression('queue-replace-failed');
      throw err;
    } finally {
      this._clearQueueReplaceSuppression('queue-replace-complete');
    }
  }

  /** True when _playTrack is actively resolving a stream URL and dispatching playback. */
  get isPlaybackInitiating(): boolean {
    return this._isPlayTrackInFlight;
  }

  /** True while a single failure-recovery decision owns advance/skip/pause. */
  isFailureRecoveryActive(): boolean {
    return this._isFailureRecoveryActive();
  }

  /**
   * Randomize upcoming tracks only (Fisher-Yates). Current track and played
   * history stay put; no transport commands — playback continues seamlessly.
   * Disabled for Ella Radio queues. Returns count shuffled, or 0 when skipped.
   */
  shuffleUpcoming(): number {
    if (isRadioQueueSession()) return 0;

    const store = useMaestroStore.getState();
    const { queue, queueIndex } = store;
    const upcomingStart = queueIndex + 1;
    const upcoming = queue.slice(upcomingStart);
    if (upcoming.length < 2) return 0;

    const currentItem = queue[queueIndex];
    const currentTrackId = currentItem?.track?.id ?? currentItem?.track?.qobuzTrackId;
    const shuffledUpcoming = fisherYatesShuffle(upcoming);
    const newQueue = [...queue.slice(0, upcomingStart), ...shuffledUpcoming];
    store.setQueue(newQueue, queueIndex);

    if (store.shuffled) {
      store.setShuffled(false, []);
    }

    const after = useMaestroStore.getState();
    const afterTrackId = after.queue[queueIndex]?.track?.id ?? after.queue[queueIndex]?.track?.qobuzTrackId;
    if (after.queueIndex !== queueIndex || afterTrackId !== currentTrackId) {
      store.setQueue(queue, queueIndex);
      return 0;
    }

    diagnosticLog('info', 'playback', 'queue_shuffled', { count: upcoming.length });
    return upcoming.length;
  }

  async playQueueTrack(newIndex: number) {
    cancelFormatCeilingReplay('user_navigation');
    void import('../radio/ellaRadioSessionState').then(({ disarmRadioAutoResume }) => {
      disarmRadioAutoResume('user_play_queue_track');
    });

    getSyncHandler().claimControl();
    TransportState.claimTransportOwner('queue');
    this._prebufferedForQueueIndex = -1;
    this._clearPrefetchedStream();
    clearGaplessPrefetchIndex();
    this.suppressAdvance(5000, 'user_skip');
    if (this._isPlayTrackInFlight) { this._isPlayTrackInFlight = false; this._inFlightTrackId = null; }
    _lastPlayTrackCompletedAt = 0;
    this._clearRecentLocalAlbumSession();

    const committed = await commitQueueOwnerPosition(newIndex);
    if (!committed?.track) return;

    await this._playTrack(committed.track, { intent: 'user' });
  }

  appendToQueue(tracks: any[], sourcePath = 'appendToQueue') {
    if (!tracks.length) return;
    let items = tracks.map((t: any) => {
      // Accept flat track objects or pre-wrapped { track } items (never double-wrap).
      if (t?.track && (t.track.id || t.track.qobuzTrackId || t.track.trackId)) {
        return t;
      }
      return { track: t };
    });
    if (isRadioQueueSession()) {
      items = filterForRadioEnqueue(items, sourcePath) as typeof items;
      if (!items.length) return;
    }
    useMaestroStore.getState().addToQueue(items);
    if (isRadioQueueSession() && items.length > 0) {
      void import('../radio/ellaRadioSessionState').then(({ notifyRadioTracksAppended }) => {
        notifyRadioTracksAppended(items.length, sourcePath);
      });
    }
  }

  /** Insert tracks to play immediately after the current queue position (live DJ moment). */
  insertPlayNext(tracks: any[], sourcePath = 'insertPlayNext') {
    if (!tracks.length) return;
    let items = tracks.map((t: any) => {
      if (t?.track && (t.track.id || t.track.qobuzTrackId || t.track.trackId)) {
        return t;
      }
      return { track: t };
    });
    if (isRadioQueueSession()) {
      items = filterForRadioEnqueue(items, sourcePath) as typeof items;
      if (!items.length) return;
    }
    this._abortGaplessPrefetch();
    console.log(
      '[QueueController] insertPlayNext — cleared SetNext prefetch (mixed-source queue mutation)',
      { inserted: items.length, sourcePath },
    );
    useMaestroStore.getState().playNext(items);
  }

  /**
   * Replace the fluid queue tail (after current + committed next tracks) without
   * touching the committed zone. Re-arms SetNext when the next-next track changes.
   */
  replaceFluidZone(tracks: any[], options?: { committedTail?: number }) {
    if (!tracks.length) return;
    const committedTail = options?.committedTail ?? 2;
    const store = useMaestroStore.getState();
    const committedEnd = store.queueIndex + 1 + committedTail;
    if (committedEnd > store.queue.length) return;

    const prevNextNext = store.queue[committedEnd - 1]?.track;
    const prevNextNextId = prevNextNext?.id || prevNextNext?.qobuzTrackId || prevNextNext?.trackId;

    const head = store.queue.slice(0, committedEnd);
    const items = tracks.map((t: any) => {
      if (t?.track && (t.track.id || t.track.qobuzTrackId || t.track.trackId)) {
        return t;
      }
      return { track: t };
    });
    const newQueue = [...head, ...items];
    store.setQueue(newQueue, store.queueIndex);

    const newNextNext = newQueue[committedEnd - 1]?.track;
    const newNextNextId = newNextNext?.id || newNextNext?.qobuzTrackId || newNextNext?.trackId;
    if (newNextNextId && newNextNextId !== prevNextNextId) {
      clearSetNextFillConfirmed();
      this._clearSetNextHandoffWatchdog();
      queueMicrotask(() => { void this._prebufferNextTrack(); });
      console.log('[QueueController] replaceFluidZone — next-next changed, re-arming SetNext prefetch');
    }
  }
}

export const QueueController = new QueueControllerImpl();

(window as any).QueueController = QueueController;

export { fisherYatesShuffle } from './queueFisherYates';
